// src/pages/ReportsStandalone.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { formatMainWithSecondary, baseCurrency, getPhase, formatMoney, eurToBgn, bgnToEur } from "../lib/currency";
import {
  Box, Paper, Stack, Select, MenuItem, TextField, Button, Tooltip,
  Typography, CircularProgress, Alert, IconButton, Tabs, Tab
} from "@mui/material";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import "dayjs/locale/bg";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";

// Alternating background for movie groups
const movieGroupBg = (i) => (i % 2 === 0 ? "#fff" : "#f0f7ff"); // white / light blue

dayjs.locale("bg");

// same logic you use elsewhere so fiscal calls work both locally and in prod
const API_BASE =
  window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
    ? "http://localhost:5000"
    : process.env.REACT_APP_API_BASE || "";

// ---- FISCAL DEVICE DATETIME (NO TZ CONVERSION) ----
const pickDeviceParts = (s) => {
  if (s == null) return null;
  const str = String(s).trim().replace("T", " ");
  // YYYY-MM-DD HH:mm[:ss][...]
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  return { Y:+m[1], M:+m[2], D:+m[3], h:+m[4], m:+m[5], s:+(m[6]||"0") };
};

const fmtDeviceDateTimeBG = (s) => {
  const p = pickDeviceParts(s);
  return p
    ? `${String(p.D).padStart(2,"0")}.${String(p.M).padStart(2,"0")}.${p.Y} ${String(p.h).padStart(2,"0")}:${String(p.m).padStart(2,"0")}:${String(p.s).padStart(2,"0")}`
    : "";
};

const fmtDeviceDayBG = (s) => {
  const p = pickDeviceParts(s);
  return p ? `${String(p.D).padStart(2,"0")}.${String(p.M).padStart(2,"0")}.${p.Y}` : "";
};

// numeric for sorting (no TZ shifts)
const deviceMsUTC = (s) => {
  const p = pickDeviceParts(s);
  return p ? Date.UTC(p.Y, p.M-1, p.D, p.h, p.m, p.s) : 0;
};

// "YYYY-MM-DD HH:mm:ss" for CSV
const deviceIso = (s) => {
  const p = pickDeviceParts(s);
  return p
    ? `${p.Y}-${String(p.M).padStart(2,"0")}-${String(p.D).padStart(2,"0")} ${String(p.h).padStart(2,"0")}:${String(p.m).padStart(2,"0")}:${String(p.s).padStart(2,"0")}`
    : "";
};

// YYYY-MM-DD (from fiscal device datetime, no TZ conversion)
const deviceYMD = (s) => {
  const p = pickDeviceParts(s);
  return p
    ? `${p.Y}-${String(p.M).padStart(2,"0")}-${String(p.D).padStart(2,"0")}`
    : "";
};

// ---- currency anchor dates (decide BGN/EUR by SALE DATE, not showtime date) ----
const rowSaleDate = (r) => {
  const ms = deviceMsUTC(r?.doc_datetime);
  if (ms) return new Date(ms);
  const b = r?.bon_datetime;
  const bm = toDayjsLoose(b);
  if (bm) return bm.toDate();
  if (r?.show_date) {
    const sm = dayjs(`${r.show_date} ${String(r.show_time || "00:00:00").slice(0, 8)}`);
    if (sm.isValid()) return sm.toDate();
  }
  return null;
};

const maxSaleDateFromRows = (arr) => {
  let max = 0;
  for (const r of (arr || [])) {
    const d = rowSaleDate(r);
    const ms = d ? d.getTime() : 0;
    if (ms > max) max = ms;
  }
  return max ? new Date(max) : null;
};

const groupAnchorDate = (g, fallbackDate) => {
  let max = 0;
  const scan = (rows) => {
    for (const d of (rows || [])) {
      const ms = deviceMsUTC(d?.doc_datetime);
      if (ms && ms > max) max = ms;
    }
  };
  if (g?.ticketDetails) for (const arr of g.ticketDetails.values()) scan(arr);
  if (g?.barDetails) for (const arr of g.barDetails.values()) scan(arr);
  return max ? new Date(max) : (fallbackDate || new Date());
};



// tolerant parser for "YYYY-MM-DD HH:mm:ss" (or with 'T')
const toDayjsLoose = (d) => {
  if (!d) return null;
  if (dayjs.isDayjs(d)) return d;
  if (typeof d === "string") {
    const s = d.includes("T") ? d : d.replace(" ", "T");
    const m = dayjs(s);
    return m.isValid() ? m : null;
  }
  const m = dayjs(d);
  return m.isValid() ? m : null;
};

const fmtHHmmss = (d) => {
  const m = toDayjsLoose(d);
  if (m) return m.format("HH:mm:ss");
  return typeof d === "string" ? d.replace("T", " ").slice(11, 19) : "";
};

const fmtBGDateTimeSec = (d) => {
  const m = toDayjsLoose(d);
  if (m) return m.format("DD.MM.YYYY HH:mm:ss");
  return typeof d === "string" ? d.replace("T", " ").slice(0, 19) : "";
};





const fmtHHmm = (t) => (t ? String(t).slice(0, 5) : "");
const fmtBGDate = (d) => (!d ? "" : (d.$d ? d.toDate() : new Date(d)).toLocaleDateString("bg-BG"));
const fmtBGDateTime = (d) => (!d ? "" : (d.$d ? d.toDate() : new Date(d)).toLocaleString("bg-BG"));
const toYMD = (d) => {
  if (!d) return "";
  const dd = d.$d ? d.toDate() : new Date(d);
  const y = dd.getFullYear();
  const m = String(dd.getMonth() + 1).padStart(2, "0");
  const da = String(dd.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
};
const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};





// ---- helpers needed by sorting/formatting ----
const toDayjs = (d) => {
  if (!d) return null;
  if (dayjs.isDayjs(d)) return d;
  if (d.$d && typeof d.toDate === "function") return dayjs(d.toDate()); // MUI DayjsAdapter obj
  if (typeof d === "string") {
    // accept "YYYY-MM-DD" and "YYYY-MM-DD HH:mm:ss" (or with T)
    const s = d.includes("T") ? d : d.replace(" ", "T");
    const m = dayjs(s);
    return m.isValid() ? m : null;
  }
  const m = dayjs(d);
  return m.isValid() ? m : null;
};

const fmtBGDay = (v) => {
  if (!v) return "";
  if (typeof v === "string") {
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/); // "YYYY-MM-DD..."
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  }
  const m = toDayjs(v);
  return m ? m.format("DD.MM.YYYY") : "";
};





/* ------------------------------ sorting utils ----------------------------- */
const SORT_FIELDS = {
  type: { label: "Тип", cmp: (a, b) => String(a.type || "").localeCompare(String(b.type || "")) },
  bon: { label: "Фискален №", cmp: (a, b) => String(a.bon_number || "").localeCompare(String(b.bon_number || "")) },
// NEW
        datetime: {
          label: "Дата/час (фиск.)",
          cmp: (a, b) => deviceMsUTC(a.doc_datetime) - deviceMsUTC(b.doc_datetime),
        },
  pay: { label: "Плащане", cmp: (a, b) => String(a.payment_method || "").localeCompare(String(b.payment_method || "")) },
  movie: { label: "Филм", cmp: (a, b) => String(a.movie_title || "").localeCompare(String(b.movie_title || "")) },
  hall: { label: "Зала", cmp: (a, b) => String(a.hall_name || "").localeCompare(String(b.hall_name || "")) },
  show: {
    label: "Прожекция",
    cmp: (a, b) => {
      const at = a.show_date && a.show_time
        ? new Date(`${a.show_date}T${String(a.show_time).slice(0,8)}`).getTime()
        : -Infinity;
      const bt = b.show_date && b.show_time
        ? new Date(`${b.show_date}T${String(b.show_time).slice(0,8)}`).getTime()
        : -Infinity;
      return at - bt;
    },
  },
  
  seat: { label: "Място", cmp: (a, b) => String(a.seat_label || "").localeCompare(String(b.seat_label || "")) },
  article: {
    label: "Артикул/Бележка",
    cmp: (a, b) =>
      String(a.article_name || (a.type === "ticket" ? "Билет" : "")).localeCompare(
        String(b.article_name || (b.type === "ticket" ? "Билет" : ""))
      ),
  },
  qty: { label: "Бр.", cmp: (a, b) => safeNum(a.quantity) - safeNum(b.quantity) },
  unit: { label: "Ед. цена", cmp: (a, b) => safeNum(a.unit_price) - safeNum(b.unit_price) },
  sum: { label: "Общо", cmp: (a, b) => safeNum(a.total_price ?? a.price) - safeNum(b.total_price ?? b.price) },
  user: { label: "Потребител", cmp: (a, b) => String(a.username || "").localeCompare(String(b.username || "")) },
};



/* ------------------------------ styled helpers ---------------------------- */
function SectionTitle({ children, sx }) {
  return <Typography sx={{ fontWeight: 700, mb: 0.5, ...sx }}>{children}</Typography>;
}
function Stat({ label, value, accent = false }) {
  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 2,
        border: "1px solid #e8e8f3",
        bgcolor: accent ? "#f0f7ff" : "#fff",
        minWidth: 160,
      }}
    >
      <div className="lbl" style={{ fontSize: 12, color: "#666" }}>{label}</div>
      <div className="val" style={{ fontWeight: 800, fontSize: 16 }}>{value}</div>
    </Box>
  );
}
function TableShell({
  children,
  padCell = "6px 8px",
  thPad = "6px 8px",
  maxHeight,
  bg = "#fff",                 // NEW
  borderColor = "#e8e8f3",     // NEW
}) {
  return (
    <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, bgcolor: bg, overflow: "hidden" }}>
      <Box sx={{ maxHeight: maxHeight || "unset", overflow: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>{children}</table>
      </Box>
    </Box>
  );
}

const Th = ({ children, pad }) => (
  <th
    style={{
      textAlign: "left",
      borderBottom: "2px solid #999",   // thicker line
      padding: pad || "6px 8px",
      whiteSpace: "nowrap",
      fontWeight: 700                   // bold header
    }}
  >
    {children}
  </th>
);
const ThRight = ({ children, pad }) => (
  <th
    style={{
      textAlign: "right",
      borderBottom: "2px solid #999",   // thicker line
      padding: pad || "6px 8px",
      whiteSpace: "nowrap",
      fontWeight: 700                   // bold header
    }}
  >
    {children}
  </th>
);


const ThCenter = ({ children, pad }) => (
  <th
    style={{
      textAlign: "center",
      borderBottom: "2px solid #999",
      padding: pad || "6px 8px",
      whiteSpace: "nowrap",
      fontWeight: 700,
    }}
  >
    {children}
  </th>
);

const TdCenter = ({ children, pad, ...rest }) => (
  <td
    {...rest}
    style={{
      textAlign: "center",
      borderBottom: "1px solid #eee",
      padding: pad || "6px 8px",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </td>
);

const Td = ({ children, pad, ...rest }) => (
  <td {...rest} style={{ borderBottom: "1px solid #eee", padding: pad || "6px 8px", whiteSpace: "nowrap" }}>{children}</td>
);
const TdRight = ({ children, pad, ...rest }) => (
  <td {...rest} style={{ textAlign: "right", borderBottom: "1px solid #eee", padding: pad || "6px 8px", whiteSpace: "nowrap" }}>{children}</td>
);
function SortableTh({ children, onClick, active, dir, pad }) {
  return (
    <th
      onClick={onClick}
      style={{
        textAlign: "left",
        borderBottom: "1px solid #999",
        padding: pad || "6px 8px",
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center">
        <span>{children}</span>
        {active ? (dir === "asc" ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />) : null}
      </Stack>
    </th>
  );
}
function SortableThRight({ children, onClick, active, dir, pad }) {
  return (
    <th
      onClick={onClick}
      style={{
        textAlign: "right",
        borderBottom: "1px solid #999",
        padding: pad || "6px 8px",
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
        <span>{children}</span>
        {active ? (dir === "asc" ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />) : null}
      </Stack>
    </th>
  );
}

/* ----------------------------- API data loaders ---------------------------- */
async function fetchMovies(cinemaId) {
  const url = `${API_BASE}/api/movies?cinema_id=${encodeURIComponent(cinemaId || "")}`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) return [];
  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? data : [];
}
async function fetchUsers(cinemaId) {
  const url = `${API_BASE}/api/users?cinema_id=${encodeURIComponent(cinemaId || "")}`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) return [];
  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? data : [];
}
async function fetchHalls(cinemaId) {
  const url = `${API_BASE}/api/halls?cinema_id=${encodeURIComponent(cinemaId || "")}`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) return [];
  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? data : [];
}
async function fetchPOSReport({ from, to, cinemaId, payment, movieId, userId, hallId, dateMode }) {
  const params = new URLSearchParams();
  if (from) params.set("from", toYMD(from));
  if (to) params.set("to", toYMD(to));
  if (cinemaId != null) params.set("cinema_id", String(cinemaId));
  if (payment) params.set("payment", payment);
  if (movieId) params.set("movie_id", String(movieId));
  if (userId) params.set("user_id", String(userId));
  if (hallId) params.set("hall_id", String(hallId));
  if (dateMode) params.set("date_mode", dateMode);

  // // не закачай самостоятелен бар към прожекции
  params.set("assign_standalone", "0");
  params.set("assign_window_pre", "20");
  params.set("assign_window_post", "10");

  const url = `${API_BASE}/api/reports/pos?${params.toString()}`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load report");
  const data = await res.json();
  return data || {};
}

/* ----------------------- normalization (columns/fields) ----------------------- */
const normalizeDirectRows = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.map((r) => {
    const isBar = r.type
      ? String(r.type).toLowerCase() === "bar"
      : r.article_id != null || r.article_name != null;
    return {
      type: isBar ? "bar" : r.type || "ticket",
      bon_number: r.bon_number ?? r.BON_NUMBER ?? null,
      bon_datetime: r.bon_datetime ?? r.BON_DATETIME ?? null,
      payment_method: (r.payment_method || r.PAYMENT_METHOD || "").toLowerCase(),
      movie_title: r.movie_title ?? r.MOVIE_TITLE ?? null,
      hall_name: r.hall_name ?? r.HALL_NAME ?? null,
      hall_id: r.hall_id ?? r.HALL_ID ?? null,
      show_date: r.show_date ?? r.SHOW_DATE ?? null,
      show_time: r.show_time ?? r.SHOW_TIME ?? null,
      seat_label: r.seat_label ?? r.SEAT_LABEL ?? null,
      category: r.category ?? r.CATEGORY ?? null,
      article_id: r.article_id ?? r.ARTICLE_ID ?? null,
      article_name: r.article_name ?? r.ARTICLE_NAME ?? (isBar ? r.text || r.description || "" : "Билет"),
      unit_price: safeNum(r.unit_price ?? r.UNIT_PRICE ?? r.price ?? r.PRICE),
      quantity: safeNum(r.quantity ?? r.QUANTITY ?? (isBar ? 1 : 1)),
      total_price: safeNum(r.total_price ?? r.TOTAL_PRICE ?? r.price ?? r.PRICE),
      username: r.username ?? r.user_name ?? r.USERNAME,
      user_id: r.user_id ?? r.USER_ID ?? null,
      movie_id: r.movie_id ?? r.MOVIE_ID ?? null,
      sale_kind: r.sale_kind ?? r.SALE_KIND ?? null,
      assigned_show_date: r.assigned_show_date ?? r.ASSIGNED_SHOW_DATE ?? null,
      assigned_show_time: r.assigned_show_time ?? r.ASSIGNED_SHOW_TIME ?? null,
      doc_no:
      r.doc_no ??
      r.docno ??
      r.DOC_NO ??
      r.DOCNO ??
      r.fiscal_docno ??
      r.device_doc_no ??
      r.DEVICE_DOC_NO ??
      null,
    
    doc_datetime:
      r.doc_datetime ??
      r.docno_datetime ??
      r.DOC_DATETIME ??
      r.DOCNO_DATETIME ??
      r.fiscal_doc_datetime ??
      r.device_doc_datetime ??
      r.DEVICE_DOC_DATETIME ??
      null,
    
      unp:
      r.unp ??
      r.device_unp ??
      r.fiscal_unp ??
      r.UNP ??
      null,
    

    };
  });
};

/* -------------------------- Quick Sale helpers -------------------------- */
const isQuickSaleRow = (r) => {
  if (r?.sale_kind === "quick") return true;
  if (String(r?.type || "").toLowerCase() === "quick") return true;
  const noShowInfo = !r?.movie_title && !r?.show_date && !r?.hall_name;
  const hint = `${r?.article_name || ""} ${r?.category || ""}`.toLowerCase();
  return noShowInfo && /(артикули|quick|бърз)/i.test(hint);
};


/* -------------------------- Storno helpers -------------------------- */
const isStornoRow = (r) => {
  const t = String(r?.type || "").toLowerCase();
  if (t === "storno" || t === "void" || t === "cancel" || t === "canceled") return true;
  if (r?.is_storno || r?.IS_STORNO) return true;
  if (String(r?.sale_kind || "").toLowerCase() === "storno") return true;
  const amt = safeNum(r?.total_price ?? r?.price);
  if (amt < 0) return true; // negative amounts => storno
  return false;
};


/* -------------------------- Bon (receipt) helpers -------------------------- */
const getBonKey = (r) => {
  const bn = String(r?.bon_number || "").trim();
  const dn = String(r?.doc_no || "").trim();
  const unp = String(r?.unp || "").trim();
  if (bn) return `bon:${bn}`;
  if (dn) return `doc:${dn}`;
  if (unp) return `unp:${unp}`;

  const dt = deviceIso(r?.doc_datetime);
  const bt = (r?.bon_datetime && dayjs(r.bon_datetime).isValid())
    ? dayjs(r.bon_datetime).format("YYYY-MM-DD HH:mm:ss")
    : "";

  const tail = [
    dt,
    bt,
    String(r?.payment_method || "").trim(),
    String(r?.username || "").trim(),
  ].filter(Boolean).join("|");

  return tail ? `other:${tail}` : "other:unknown";
};

const isPromoTicketRow = (r) => {
  const t = String(r?.type || "").toLowerCase();
  if (t !== "ticket") return false;
  if (isQuickSaleRow(r)) return false;

  const kind = String(r?.sale_kind || "").toLowerCase();
  if (kind === "promo") return true;

  const price = safeNum(r?.total_price ?? r?.price ?? 0);
  return price === 0;
};


/* --------------------------------- Component --------------------------------- */
export default function ReportsStandalone({ cinemaId: propCinemaId }) {
  const [from, setFrom] = useState(dayjs());
  const [to, setTo] = useState(dayjs());
  const [cinemaId, setCinemaId] = useState(propCinemaId || "");
  const [payment, setPayment] = useState("all");
  const [movieId, setMovieId] = useState("");
  const [userId, setUserId] = useState("");
  const [hallId, setHallId] = useState("");
  const [dateMode, setDateMode] = useState("sale");
  // "sale"  -> date_mode = sale (по дата на бона)
  // "showtime" -> date_mode = show (по дата на прожекцията)
  const [viewTab, setViewTab] = useState("sale");

  useEffect(() => {
    // Tab 1: по дата на продажба
    // Tab 2: по дата на прожекцията (show_date)
    setDateMode(viewTab === "showtime" ? "show" : "sale");
  }, [viewTab]);



  const [rows, setRows] = useState([]);
  const [stornoBons, setStornoBons] = useState([]); // 🔹 отделно
  // When clicking a storno bon, we highlight the ORIGINAL bon (storno_of_unp) in the printed-bons list.
  const [stornoFocusUnp, setStornoFocusUnp] = useState("");
  const bonRowRefs = useRef(new Map());
  const [movies, setMovies] = useState([]);
  const [users, setUsers] = useState([]);
  const [halls, setHalls] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [reportKind, setReportKind] = useState("all"); // all|tickets|bar|mixed_bons|ticket_only_bons|bar_only_bons|quick|quick_only_bons|promo_tickets|paid_tickets|bar_with_show|bar_standalone
  const [groupBy, setGroupBy] = useState("day"); // none|day|movie|showtime|user
  const [sortField, setSortField] = useState("datetime");
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");

// --- storno netting (what to EXCLUDE from sold) ---
// We treat FULL storno as "cancel the whole original bon".
// Partial storno should NOT hide the original rows; it just reduces net totals.
const stornoSets = useMemo(() => {
  // Aggregate storno by original UNP so we can detect FULL vs PARTIAL storno.
  const stornoAbsByUnp = new Map(); // unp -> abs(storno sum)
  const stornoDocNos = new Set();   // storno receipt numbers (so we don't count them as sales)

  for (const s of (stornoBons || [])) {
    const refUnp = String(s?.storno_of_unp || s?.storno_of_unp_code || "").trim();
    const amtAbs = Math.abs(safeNum(s?.total_amount ?? 0));
    if (refUnp && amtAbs) stornoAbsByUnp.set(refUnp, (stornoAbsByUnp.get(refUnp) || 0) + amtAbs);

    const doc = String(s?.bon_number || s?.doc_no || "").trim();
    if (doc) stornoDocNos.add(doc);
  }

  // Sum originals by UNP from the loaded rows (non-storno rows only)
  const origSumByUnp = new Map();
  for (const r of (rows || [])) {
    if (!r || isStornoRow(r)) continue;
    const unp = String(r.unp || r.bon_number || "").trim();
    if (!unp) continue;
    const val = safeNum(r.total_price ?? r.price ?? 0);
    origSumByUnp.set(unp, (origSumByUnp.get(unp) || 0) + val);
  }

  const fullCanceledByUnp = new Set();
  const partialByUnp = new Set();

  for (const [unp, stornoAbs] of stornoAbsByUnp.entries()) {
    const orig = origSumByUnp.get(unp) || 0;
    if (orig > 0 && stornoAbs >= orig - 0.01) fullCanceledByUnp.add(unp);
    else partialByUnp.add(unp);
  }

  return { stornoAbsByUnp, fullCanceledByUnp, partialByUnp, stornoDocNos };
}, [stornoBons, rows]);

// Jump to the referenced bon in the printed list when a storno is clicked
useEffect(() => {
  if (!stornoFocusUnp) return;
  const el = bonRowRefs.current.get(stornoFocusUnp);
  if (el && typeof el.scrollIntoView === "function") {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}, [stornoFocusUnp]);

// Index storno rows by their referenced original UNP (for UI linking)
const stornoByUnp = useMemo(() => {
  const m = new Map();
  for (const s of (stornoBons || [])) {
    const unp = String(s?.storno_of_unp || s?.storno_of_unp_code || "").trim();
    if (!unp) continue;
    const arr = m.get(unp) || [];
    arr.push(s);
    m.set(unp, arr);
  }
  return m;
}, [stornoBons]);

// Keep only rows that truly count as sold (storno removed)
const rowsNet = useMemo(() => {
  return (rows || []).filter(r => {
    if (!r) return false;

    // 1) drop explicit storno/negative rows
    if (isStornoRow(r)) return false;

    // 2) drop storno documents themselves (just in case backend included them)
    const docNo = String(r.doc_no || "").trim();
    const bonNo = String(r.bon_number || "").trim();
    if ((docNo && stornoSets.stornoDocNos.has(docNo)) ||
        (bonNo && stornoSets.stornoDocNos.has(bonNo))) {
      return false;
    }

    // 3) drop originals that were storno’d by UNP
    const unp = String(r.unp || "").trim();
    if (unp && stornoSets.fullCanceledByUnp.has(unp)) return false;

    return true;
  });
}, [rows, stornoSets]);

// Base filters WITHOUT removing originals that were later storno'd.
// This is used for the "Printed bons" list (so operators can see what was printed, even if it was canceled).
const basicRowsAll = useMemo(() => {
  let out = Array.isArray(rows) ? rows : [];

  const pay = String(payment || "all").toLowerCase();
  if (pay && pay !== "all") {
    out = out.filter((r) => String(r?.payment_method || "").toLowerCase() === pay);
  }

  const uid = userId ? String(userId) : "";
  if (uid) {
    out = out.filter((r) => String(r?.user_id ?? "") === uid);
  }

  const hid = hallId ? String(hallId) : "";
  if (hid) {
    const hallObj = (halls || []).find(
      (h) => String(h.HALL_ID ?? h.hall_id ?? h.id ?? "") === hid
    );
    const hallName = String(hallObj?.HALL_NAME ?? hallObj?.hall_name ?? hallObj?.name ?? "")
      .trim()
      .toLowerCase();
    out = out.filter((r) =>
      String(r?.hall_id ?? "") === hid ||
      (hallName && String(r?.hall_name || "").trim().toLowerCase() === hallName)
    );
  }

  const mid = movieId ? String(movieId) : "";
  if (mid) {
    const mObj = (movies || []).find(
      (m) => String(m.id ?? m.movie_id ?? m.MOVIE_ID ?? "") === mid
    );
    const mTitle = String(mObj?.title ?? mObj?.name ?? mObj?.movie_title ?? mObj?.MOVIE_TITLE ?? "")
      .trim()
      .toLowerCase();
    out = out.filter((r) =>
      String(r?.movie_id ?? "") === mid ||
      (mTitle && String(r?.movie_title || "").trim().toLowerCase() === mTitle)
    );
  }

  // When "По прожекция" (dateMode=show): filter by show_date; rows without show_date fall back to SALE day.
  if (dateMode === "show") {
    const fromY = toYMD(from);
    const toY = toYMD(to);
    if (fromY && toY) {
      out = out.filter((r) => {
        const sd = String(r?.show_date || r?.assigned_show_date || "").slice(0, 10);
        if (sd) return sd >= fromY && sd <= toY;

        const saleY =
          (r?.doc_datetime ? deviceYMD(r.doc_datetime) : "") ||
          (toDayjsLoose(r?.bon_datetime)?.format("YYYY-MM-DD") || "");
        if (!saleY) return false;
        return saleY >= fromY && saleY <= toY;
      });
    }
  }

  return out;
}, [rows, payment, userId, hallId, movieId, dateMode, from, to, halls, movies]);

// Printed bons list (grouped by UNP/bon number), shows also canceled (storno'd) originals.
const printedBons = useMemo(() => {
  const map = new Map();
  for (const r of (basicRowsAll || [])) {
    if (!r) continue;
    const unp = String(r.unp || "").trim();
    const bonNo = String(r.bon_number || "").trim();
    const key = unp || bonNo || getBonKey(r);
    const curr = map.get(key) || {
      key,
      unp,
      bon_number: bonNo,
      bon_datetime: r.bon_datetime || null,
      doc_datetime: r.doc_datetime || null,
      payment_method: r.payment_method || "",
      username: r.username || "",
      sum: 0,
      tickets: 0,
      bar: 0,
      quick: 0,
    };

    // prefer fiscal device datetime if present
    if (!curr.doc_datetime && r.doc_datetime) curr.doc_datetime = r.doc_datetime;
    if (!curr.bon_datetime && r.bon_datetime) curr.bon_datetime = r.bon_datetime;
    if (!curr.payment_method && r.payment_method) curr.payment_method = r.payment_method;
    if (!curr.username && r.username) curr.username = r.username;
    if (!curr.bon_number && bonNo) curr.bon_number = bonNo;
    if (!curr.unp && unp) curr.unp = unp;

    const val = safeNum(r.total_price ?? r.price ?? 0);
    curr.sum += val;

    const t = String(r.type || "").toLowerCase();
    if (isQuickSaleRow(r)) curr.quick += val;
    else if (t === "ticket") curr.tickets += val;
    else if (t === "bar") curr.bar += val;

    map.set(key, curr);
  }

  const list = Array.from(map.values()).map((b) => {
    const unpKey = String(b.unp || b.bon_number || "").trim();
    const stornoList = stornoByUnp.get(unpKey) || [];
    const isCanceled = !!(unpKey && stornoSets.fullCanceledByUnp.has(unpKey));
    return {
      ...b,
      unpKey,
      isCanceled,
      storno_numbers: stornoList.map((s) => s?.bon_number).filter(Boolean),
    };
  });

  // Sort by fiscal time desc (doc_datetime), then by bon_datetime
  list.sort((a, b) => {
    const at = deviceMsUTC(a.doc_datetime) || (toDayjsLoose(a.bon_datetime)?.valueOf() || 0);
    const bt = deviceMsUTC(b.doc_datetime) || (toDayjsLoose(b.bon_datetime)?.valueOf() || 0);
    return bt - at;
  });
  return list;
}, [basicRowsAll, stornoByUnp, stornoSets]);


// Apply base filters consistently (used for UI + exports + prints)
const basicRows = useMemo(() => {
  let out = Array.isArray(rowsNet) ? rowsNet : [];

  const pay = String(payment || "all").toLowerCase();
  if (pay && pay !== "all") {
    out = out.filter((r) => String(r?.payment_method || "").toLowerCase() === pay);
  }

  const uid = userId ? String(userId) : "";
  if (uid) {
    out = out.filter((r) => String(r?.user_id ?? "") === uid);
  }

  const hid = hallId ? String(hallId) : "";
  if (hid) {
    const hallObj = (halls || []).find(
      (h) => String(h.HALL_ID ?? h.hall_id ?? h.id ?? "") === hid
    );
    const hallName = String(hallObj?.HALL_NAME ?? hallObj?.hall_name ?? hallObj?.name ?? "")
      .trim()
      .toLowerCase();
    out = out.filter((r) =>
      String(r?.hall_id ?? "") === hid ||
      (hallName && String(r?.hall_name || "").trim().toLowerCase() === hallName)
    );
  }

  const mid = movieId ? String(movieId) : "";
  if (mid) {
    const mObj = (movies || []).find(
      (m) => String(m.id ?? m.movie_id ?? m.MOVIE_ID ?? "") === mid
    );
    const mTitle = String(mObj?.title ?? mObj?.name ?? mObj?.movie_title ?? mObj?.MOVIE_TITLE ?? "")
      .trim()
      .toLowerCase();
    out = out.filter((r) =>
      String(r?.movie_id ?? "") === mid ||
      (mTitle && String(r?.movie_title || "").trim().toLowerCase() === mTitle)
    );
  }

  // When "По прожекция" (dateMode=show): filter by show_date; rows without show_date fall back to SALE day.
  if (dateMode === "show") {
    const fromY = toYMD(from);
    const toY = toYMD(to);
    if (fromY && toY) {
      out = out.filter((r) => {
        const sd = String(r?.show_date || r?.assigned_show_date || "").slice(0, 10);
        if (sd) return sd >= fromY && sd <= toY;

        const saleY =
          (r?.doc_datetime ? deviceYMD(r.doc_datetime) : "") ||
          (toDayjsLoose(r?.bon_datetime)?.format("YYYY-MM-DD") || "");
        if (!saleY) return false;
        return saleY >= fromY && saleY <= toY;
      });
    }
  }

  return out;
}, [rowsNet, payment, userId, hallId, movieId, dateMode, from, to, halls, movies]);


// Profile each receipt (bon) so we can filter "mixed" / "only tickets" / etc.
const bonProfile = useMemo(() => {
  const map = new Map();
  for (const r of (basicRows || [])) {
    if (!r) continue;
    const key = getBonKey(r);
    const p = map.get(key) || { hasTicket: false, hasBar: false, hasQuick: false, hasPromoTicket: false };
    const quick = isQuickSaleRow(r);
    const t = String(r.type || "").toLowerCase();

    if (quick) {
      p.hasQuick = true;
    } else if (t === "ticket") {
      p.hasTicket = true;
      if (isPromoTicketRow(r)) p.hasPromoTicket = true;
    } else if (t === "bar") {
      p.hasBar = true;
    }
    map.set(key, p);
  }
  return map;
}, [basicRows]);


// --- expand/collapse state for per-item details ---
const [openDetailKeys, setOpenDetailKeys] = useState(() => new Set());

// --- expand/collapse state for per-show details (in the flat list) ---
const [openShowKeys, setOpenShowKeys] = useState(() => new Set());
const toggleShowOpen = (key) => {
  setOpenShowKeys((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
};
const isShowOpen = (key) => openShowKeys.has(key);

const toggleOpen = (key) => {
  setOpenDetailKeys(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
};
const isOpen = (key) => openDetailKeys.has(key);

// tiny caret icon helper
const Caret = ({ open }) => open ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />;


  /* ---------------------- initial movies/users --------------------- */
  useEffect(() => {
    let alive = true;
    (async () => {
      const [ms, us, hs] = await Promise.all([fetchMovies(cinemaId), fetchUsers(cinemaId), fetchHalls(cinemaId)]);
      if (!alive) return;
      setMovies(ms || []);
      setUsers(us || []);
      setHalls(hs || []);
    })();
    return () => { alive = false; };
  }, [cinemaId]);

  /* ------------------------- load main POS report data ------------------------- */
  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchPOSReport({
        from, to, cinemaId,
        payment,
        movieId,
        userId,
        hallId,
        dateMode,
      });
      const normalized = normalizeDirectRows(data?.rows || []);
      setRows(normalized);
      setStornoBons(Array.isArray(data?.storno) ? data.storno : []);
    } catch (err) {
      console.error(err);
      setError("Грешка при зареждане на отчета.");
    } finally {
      setLoading(false);
    }
  }, [from, to, cinemaId, payment, movieId, userId, hallId, dateMode]);

  useEffect(() => { loadData(); }, [loadData]);

  /* ------------------------------ client filters ------------------------------ */
  // filteredRows is the SINGLE source of truth used by UI + exports + prints
  const filteredRows = useMemo(() => {
    let out = Array.isArray(basicRows) ? basicRows : [];

    const kind = String(reportKind || "all").toLowerCase();
    out = out.filter((r) => {
      const t = String(r?.type || "").toLowerCase();
      const quick = isQuickSaleRow(r);
      const ticket = !quick && t === "ticket";
      const bar = !quick && t === "bar";
      const p = bonProfile.get(getBonKey(r)) || {};
      const promo = ticket && (String(r?.sale_kind || "").toLowerCase() === "promo" || safeNum(r?.total_price ?? r?.price ?? 0) === 0);

      if (kind === "all" || kind === "both") return true;
      if (kind === "tickets") return ticket;
      if (kind === "bar") return bar;
      if (kind === "quick") return quick;

      if (kind === "mixed_bons") return (ticket || bar) && !!(p.hasTicket && p.hasBar);
      if (kind === "ticket_only_bons") return ticket && !!(p.hasTicket && !p.hasBar && !p.hasQuick);
      if (kind === "bar_only_bons") return bar && !!(p.hasBar && !p.hasTicket && !p.hasQuick);
      if (kind === "quick_only_bons") return quick && !!(p.hasQuick && !p.hasTicket && !p.hasBar);

      if (kind === "promo_tickets") return promo;
      if (kind === "paid_tickets") return ticket && safeNum(r?.total_price ?? r?.price ?? 0) > 0;

      if (kind === "bar_with_show") return bar && !!(r?.show_date && r?.show_time);
      if (kind === "bar_standalone") return bar && !(r?.show_date && r?.show_time);

      return true;
    });

    const q = (search || "").trim().toLowerCase();
    if (q) {
      out = out.filter((r) => {
        const text = [
          r.bon_number, r.payment_method, r.movie_title, r.hall_name, r.show_date, r.show_time,
          r.seat_label, r.category, r.article_name, r.username,
          r.doc_no, r.doc_datetime,
        ]
          .map((x) => String(x || ""))
          .join(" ")
          .toLowerCase();
        return text.includes(q);
      });
    }

    return out;
  }, [basicRows, reportKind, search, bonProfile]);



  const movieOptions = useMemo(() => {
    const fromY = toYMD(from);
    const toY   = toYMD(to);
    const byKey = new Map();
  
    for (const r of filteredRows) {
      if (!r.show_date || !r.show_time) continue;          // only rows tied to a show
      if (dateMode === "show" && (r.show_date < fromY || r.show_date > toY)) continue;
  
      const title = (r.movie_title || "").trim();
      const mid   = r.movie_id ?? null;
      if (!title && mid == null) continue;
  
      const key = mid != null ? `id:${mid}` : `t:${title.toLowerCase()}`;
      if (!byKey.has(key)) byKey.set(key, { value: mid != null ? String(mid) : `title::${title}`, title, movie_id: mid });
    }
  
    return Array.from(byKey.values())
      .sort((a, b) => String(a.title || "").localeCompare(String(b.title || ""), "bg"));
  }, [basicRows, from, to, dateMode]);
  

  const onPickMovie = (val) => {
    if (!val) return setMovieId("");
    if (String(val).startsWith("title::")) {
      setMovieId("");
      setSearch(String(val).slice(8)); // narrow with search by title
    } else {
      setMovieId(val);                 // real movie_id
    }
  };


  /* ----------------------------- totals + grouping ---------------------------- */
  const simpleData = useMemo(() => {
    const filtered = filteredRows;

    const sorter = SORT_FIELDS[sortField]?.cmp || (() => 0);
    const sortedRows = [...filtered].sort((a, b) => {
      const res = sorter(a, b);
      return sortDir === "asc" ? res : -res;
    });

    const keyFns = {
      none: () => "_all_",
      day: (r) => fmtDeviceDayBG(r.doc_datetime) || fmtBGDay(r.show_date),
      movie: (r) => r.movie_title || "(без филм)",
      showtime: (r) => {
        const d = r.show_date ? dayjs(`${r.show_date} ${r.show_time || "00:00"}`) : null;
        return d ? d.format("DD.MM.YYYY HH:mm") : "(без прожекция)";
      },
      user: (r) => r.username || r.user_name || "(без потребител)",
    };
    const keyFn = keyFns[groupBy] || keyFns.none;

    const map = new Map();
    for (const r of sortedRows) {
      const k = keyFn(r);
      const acc = map.get(k) || { key: k, qty: 0, sum: 0, tickets: 0, bar: 0, quick: 0 };
      const q = safeNum(r.quantity || 1);
      const val = safeNum(r.total_price ?? r.price ?? 0);
      acc.qty += q;
      acc.sum += val;
      if (isQuickSaleRow(r)) acc.quick += val;
      else if (r.type === "ticket") acc.tickets += val;
      else acc.bar += val;
      map.set(k, acc);
    }

    const list = Array.from(map.values()).sort((a, b) => String(a.key).localeCompare(String(b.key)));
    const totals = list.reduce((acc, it) => {
      acc.qty += it.qty;
      acc.sum += it.sum;
      acc.tickets += it.tickets;
      acc.bar += it.bar;
      acc.quick += it.quick;
      return acc;
    }, { qty: 0, sum: 0, tickets: 0, bar: 0, quick: 0 });

    return { list, totals, sortedRows };
  }, [filteredRows, groupBy, sortField, sortDir]);


  // Currency in reports is determined by the SALE date (fiscal device datetime),
  // so pre-sales in 2025 remain in BGN even if the showtime is in 2026.
  const reportAnchorDate = useMemo(() => {
    const d = maxSaleDateFromRows(simpleData.sortedRows);
    if (d) return d;
    const t = toDayjsLoose(to);
    return t ? t.toDate() : new Date();
  }, [simpleData.sortedRows, to]);

  /* --------------------------- Бар категории --------------------------- */
  const BAR_CATEGORIES = [
    { key: "Топли напитки", hints: [/кафе/i, /кaпучино/i, /капучино/i, /espresso/i, /еспресо/i, /чай/i, /чая/i, /лате/i, /какао/i, /горещ/i] },
    { key: "Студени напитки", hints: [/вода/i, /cola/i, /кока/i, /фанта/i, /sprite/i, /сок/i, /айрян/i, /студен/i, /леден/i, /лимонада/i] },
    { key: "Пуканки", hints: [/пуканк/i, /popcorn/i] },
  ];
  const getBarCategory = (name) => {
    const n = String(name || "").trim();
    for (const c of BAR_CATEGORIES) if (c.hints.some((rx) => rx.test(n))) return c.key;
    return "Друго";
  };

  /* ---------------------- Междинни отчети по прожекция ---------------------- */
  const showtimeData = useMemo(() => {
    const fromYMD = toYMD(from);
    const toYMD_  = toYMD(to);

    const groupsMap = new Map();
    const standaloneBar = { qty: 0, sum: 0, byCat: new Map(), items: new Map() };
    for (const r of filteredRows) {

      if (!r) continue;
      if (isQuickSaleRow(r)) continue;

      const isBar = String(r.type || "").toLowerCase() === "bar";


      const hasShow = !!(r.show_date && r.show_time);

      if (dateMode === "sale" && hasShow) {
        // keep — already filtered by backend window
      }

      const val = safeNum(r.total_price ?? r.price ?? 0);
      const qty = safeNum(r.quantity ?? 1);

      // INSIDE the for (const r of rows) loop, in the `if (!hasShow) { ... }` branch:

      if (!hasShow) {
        if (isBar) {
          const cat = getBarCategory(r.article_name || r.category || "");
          const val = safeNum(r.total_price ?? r.price ?? 0);
          const qty = safeNum(r.quantity ?? 1);
      
          // Time label & date from fiscal device, shown EXACTLY as printed on device.
// Prefer device_doc_datetime (backend -> r.doc_datetime). If missing, fall back to bon_datetime.
// We DO NOT pass these through dayjs/new Date to avoid timezone shifts.
let timeLabel = "";
let dateLabel = "";

// Replace your current pickParts with this:
const pickParts = (s) => {
  if (s == null) return null;
  const str = String(s).trim().replace("T", " ");
  // Match: YYYY-MM-DD HH:mm[:ss][...anything (ms, Z, offset)]
  const m = str.match(
    /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?/
  );
  if (!m) return null;
  return { Y: m[1], M: m[2], D: m[3], h: m[4], m: m[5], s: m[6] || "00" };
};


const use = pickParts(r.doc_datetime); // only fiscal device time


if (use) {
  dateLabel = `${use.D}.${use.M}.${use.Y}`;
  timeLabel = `${use.h}:${use.m}:${use.s}`;
}


const docNo = r.doc_no || "";
const unp = r.unp || "";

// Category roll-up
const acc = standaloneBar.byCat.get(cat) || { key: cat, qty: 0, sum: 0 };
acc.qty += qty; acc.sum += val;
standaloneBar.byCat.set(cat, acc);

// Per-(date,time,doc,item) roll-up (so cashier sees when/which document)
const iname = String(r.article_name || "Артикул").trim();
const itemKey = `${dateLabel}|${timeLabel}|${docNo}|${iname}`;
const si = standaloneBar.items.get(itemKey) || {
  name: iname,
  date: dateLabel,
  time: timeLabel,
  doc_no: docNo,
  unp,                                      // ✅ store UNP
  qty: 0,
  sum: 0
};
si.qty += qty; si.sum += val;
standaloneBar.items.set(itemKey, si);

standaloneBar.qty += qty;
standaloneBar.sum += val;

        }
        continue;
      }
      


 // group by date+time+hall so each showtime (even same movie) is its own block
 const key = `${r.show_date} ${r.show_time} ${r.hall_name || ""}`;
      const obj = groupsMap.get(key) || {
        key,
        movie_title: r.movie_title || "(без филм)",
        show_date: r.show_date || null,
        show_time: r.show_time || null,
        hall_name: r.hall_name || null,
        bar: { total: 0, qty: 0, byCat: new Map() },
        tickets: { total: 0, qty: 0 },
        barItems: new Map(),
        ticketItems: new Map(),
          // NEW: detail collections used by dropdowns
   ticketDetails: new Map(),  // key: ticket item name -> array of rows
   barDetails: new Map(),     // key: bar item name -> array of rows
      };

      if (isBar) {
        const cat = getBarCategory(r.article_name || r.category || "");
        const acc = obj.bar.byCat.get(cat) || { key: cat, qty: 0, sum: 0 };
        acc.qty += qty; acc.sum += val;
        obj.bar.byCat.set(cat, acc);
        obj.bar.total += val;
        obj.bar.qty += qty;

        const iname = String(r.article_name || "Артикул").trim();
        const bi = obj.barItems.get(iname) || { name: iname, qty: 0, sum: 0 };
        bi.qty += qty; bi.sum += val;
        obj.barItems.set(iname, bi);

         // NEW: per-row details for dropdown
         const bdet = obj.barDetails.get(iname) || [];
         bdet.push({
           bon_number: r.bon_number || "",
           unp: r.unp || "",
           doc_no: r.doc_no || "",
           doc_datetime: r.doc_datetime || r.bon_datetime || null,
           qty: Number(r.quantity ?? 1),
           unit_price: Number(r.unit_price ?? r.price ?? 0),
           sum: Number(r.total_price ?? r.price ?? 0),
           username: r.username || ""
         });
         obj.barDetails.set(iname, bdet);


      } else {
        obj.tickets.total += val;
        obj.tickets.qty += qty;

        const tname = r.category ? `Билет — ${r.category}` : "Билет";
        const ti = obj.ticketItems.get(tname) || { name: tname, qty: 0, sum: 0 };
        ti.qty += 1; ti.sum += val;
        obj.ticketItems.set(tname, ti);
         // NEW: per-row details for dropdown
 const tdet = obj.ticketDetails.get(tname) || [];
 tdet.push({
   bon_number: r.bon_number || "",
   unp: r.unp || "",
   doc_no: r.doc_no || "",
   doc_datetime: r.doc_datetime || r.bon_datetime || null,
   seat_label: r.seat_label || "",
   price: Number(r.total_price ?? r.price ?? 0),
   username: r.username || ""
 });
 obj.ticketDetails.set(tname, tdet);
      }

      groupsMap.set(key, obj);
    }

    const groups = Array.from(groupsMap.values()).sort((a, b) => {
      const as = `${a.show_date || ""} ${a.show_time || "00:00"}`;
      const bs = `${b.show_date || ""} ${b.show_time || "00:00"}`;
      if (as < bs) return -1;
      if (as > bs) return 1;
      return (a.hall_name || "").localeCompare(b.hall_name || "");
    });

    for (const g of groups) {
      const ordered = [];
      for (const c of BAR_CATEGORIES.map(x => x.key)) if (g.bar.byCat.has(c)) ordered.push(g.bar.byCat.get(c));
      for (const [k, v] of g.bar.byCat.entries()) if (!BAR_CATEGORIES.find(x => x.key === k)) ordered.push(v);
      g.bar.byCatArr = ordered;

      g.barItemsArr = Array.from(g.barItems.values())
        .sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name, "bg"));
      g.ticketItemsArr = Array.from(g.ticketItems.values())
        .sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name, "bg"));
    }




    // Standalone bar arrays (order by qty desc, then name)
    const standaloneBarItemsArr = Array.from(standaloneBar.items.values())
    .sort((a, b) => {
      // date asc (empty last)
      const da = a.date || "99.99.9999";
      const db = b.date || "99.99.9999";
      if (da !== db) return da.localeCompare(db, "bg");
      // time asc (empty last)
      const ta = a.time || "99:99";
      const tb = b.time || "99:99";
      if (ta !== tb) return ta.localeCompare(tb);
      // doc no asc
      const dna = a.doc_no || "";
      const dnb = b.doc_no || "";
      if (dna !== dnb) return dna.localeCompare(dnb, "bg");
      // then qty desc, then name asc
      if (b.qty !== a.qty) return b.qty - a.qty;
      return a.name.localeCompare(b.name, "bg");
    });
  
  
  const standaloneBarByCatArr = Array.from(standaloneBar.byCat.values())
    .sort((a, b) => b.qty - a.qty || a.key.localeCompare(b.key, "bg"));
  
    const totals = groups.reduce((acc, g) => {
      acc.bar.qty += g.bar.qty; acc.bar.sum += g.bar.total;
      acc.tickets.qty += g.tickets.qty; acc.tickets.sum += g.tickets.total;
      return acc;
    }, { bar: { qty: 0, sum: 0 }, tickets: { qty: 0, sum: 0 } });

    totals.bar.qty += standaloneBar.qty;
    totals.bar.sum += standaloneBar.sum;

    return { groups, standaloneBar, standaloneBarItemsArr, standaloneBarByCatArr, totals };
  }, [filteredRows, from, to, dateMode]);

  // --- summary grouped by movie -> showtime (with per-movie totals) ---
  const movieShowSummary = useMemo(() => {
    const groups = (showtimeData?.groups || []).slice();

    const m = new Map();
    for (const g of groups) {
      const title = (g.movie_title || "(без филм)").trim();
      const mg = m.get(title) || {
        movie_title: title,
        shows: [],
        totals: { tickets: 0, bar: 0, qty: 0, grand: 0 },
      };
      mg.shows.push(g);
      mg.totals.tickets += g.tickets.total;
      mg.totals.bar     += g.bar.total;
      mg.totals.qty     += (g.tickets.qty + g.bar.qty);
      mg.totals.grand   += (g.tickets.total + g.bar.total);
      m.set(title, mg);
    }

    for (const mg of m.values()) {
      mg.shows.sort((a, b) => {
        const as = `${a.show_date || ""} ${a.show_time || "00:00"}`;
        const bs = `${b.show_date || ""} ${b.show_time || "00:00"}`;
        if (as < bs) return -1;
        if (as > bs) return 1;
        return (a.hall_name || "").localeCompare(b.hall_name || "", "bg");
      });
    }

    const arr = Array.from(m.values());
    arr.sort((A, B) => {
      const a0 = A.shows[0], b0 = B.shows[0];
      if (a0 && b0) {
        const as = `${a0.show_date || ""} ${a0.show_time || "00:00"}`;
        const bs = `${b0.show_date || ""} ${b0.show_time || "00:00"}`;
        if (as < bs) return -1;
        if (as > bs) return 1;
      }
      return A.movie_title.localeCompare(B.movie_title, "bg");
    });

    const rollup = arr.reduce((acc, mg) => {
      acc.tickets += mg.totals.tickets;
      acc.bar     += mg.totals.bar;
      acc.qty     += mg.totals.qty;
      acc.grand   += mg.totals.grand;
      return acc;
    }, { tickets: 0, bar: 0, qty: 0, grand: 0 });

    return { movies: arr, totals: rollup };
  }, [showtimeData]);

  // --- Quick sales summary (by article) ---
  const quickData = useMemo(() => {
    // respect current filters (reportKind + search)
    const base = filteredRows;

    const map = new Map();      // агрегирани по артикул
    const details = new Map();  // детайли по артикул -> [редове]
    const totals = { qty: 0, sum: 0 };

    for (const r of base) {
      if (!isQuickSaleRow(r)) continue;

      const name = (r.article_name || r.description || "Бърза продажба").trim();
      const qty  = safeNum(r.quantity || 1);
      const sum  = safeNum(r.total_price ?? r.price ?? 0);

      // единична цена (fallback, ако някога е 0)
      const rawUnit = safeNum(r.unit_price ?? r.price);
      const unit    = rawUnit || (qty ? sum / qty : sum);

      totals.qty += qty;
      totals.sum += sum;

      // агрегирано по артикул
      const it = map.get(name) || { name, qty: 0, sum: 0 };
      it.qty += qty;
      it.sum += sum;
      map.set(name, it);

      // детайли по бон
      const arr = details.get(name) || [];
      arr.push({
        bon_number:   r.bon_number || "",
        unp:          r.unp || "",
        doc_no:       r.doc_no || "",
        doc_datetime: r.doc_datetime || r.bon_datetime || null,
        qty,
        unit_price: unit,
        sum,
        username: r.username || "",
      });
      details.set(name, arr);
    }

    const items = Array.from(map.values())
      .sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name, "bg"));

    return { items, totals, details };
  }, [filteredRows]);


  // --- NEW: Bar sales summary (by article) for the selected period ---
  // Includes BOTH:
  //  - standalone bar (no showtime / no tickets)
  //  - bar sold together with tickets (or otherwise linked to a show)
  const barSalesSummary = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();

    const matchesSearch = (r) => {
      if (!q) return true;
      const hay = [
        r.article_name,
        r.description,
        r.doc_no,
        r.unp,
        r.bon_number,
        r.username,
        r.movie_title,
        r.hall_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    };

    const base = filteredRows
      .filter((r) => String(r?.type || "").toLowerCase() === "bar")
      .filter((r) => !isQuickSaleRow(r));

    const map = new Map();      // item -> aggregates
    const details = new Map();  // item -> [detail rows]
    const totals = {
      standaloneQty: 0,
      standaloneSum: 0,
      withTicketQty: 0,
      withTicketSum: 0,
      qty: 0,
      sum: 0,
    };

    for (const r of base) {
      const name = (r.article_name || r.description || "Бар артикул").trim();

      const qty = safeNum(r.quantity || 1);
      const sum = safeNum(r.total_price ?? r.price ?? 0);

      // единична цена (fallback, ако някога е 0)
      const rawUnit = safeNum(r.unit_price ?? r.price);
      const unit = rawUnit || (qty ? sum / qty : sum);

      // treat bar as "with tickets" if it is linked to a show
      const showDate = r.show_date || r.assigned_show_date;
      const showTime = r.show_time || r.assigned_show_time;
      const withTicket = !!(showDate && showTime);

      // aggregate
      const it = map.get(name) || {
        name,
        standaloneQty: 0,
        standaloneSum: 0,
        withTicketQty: 0,
        withTicketSum: 0,
        qty: 0,
        sum: 0,
      };

      if (withTicket) {
        it.withTicketQty += qty;
        it.withTicketSum += sum;
        totals.withTicketQty += qty;
        totals.withTicketSum += sum;
      } else {
        it.standaloneQty += qty;
        it.standaloneSum += sum;
        totals.standaloneQty += qty;
        totals.standaloneSum += sum;
      }

      it.qty += qty;
      it.sum += sum;
      totals.qty += qty;
      totals.sum += sum;

      map.set(name, it);

      // details (for caret expand)
      const arr = details.get(name) || [];
      arr.push({
        kind: withTicket ? "bar_with_ticket" : "bar_standalone",
        bon_number: r.bon_number || "",
        unp: r.unp || "",
        doc_no: r.doc_no || "",
        doc_datetime: r.doc_datetime || r.bon_datetime || null,
        qty,
        unit_price: unit,
        sum,
        username: r.username || "",
        show_date: showDate || "",
        show_time: showTime || "",
        movie_title: r.movie_title || "",
        hall_name: r.hall_name || "",
      });
      details.set(name, arr);
    }

    const items = Array.from(map.values()).sort(
      (a, b) => b.sum - a.sum || b.qty - a.qty || a.name.localeCompare(b.name, "bg")
    );

    return { items, totals, details };
  }, [filteredRows, search]);


  // --- NEW: Sales by movie per day (only fiscal tickets) ---
    // --- NEW: Sales by showtime (only fiscal tickets) ---
    const showSalesData = useMemo(() => {
      // спазваме текущите филтри (search, reportKind и т.н.)
      const base = filteredRows;

      const map = new Map(); // key: `${show_date} ${show_time} ${movie} ${hall}`

      for (const r of base) {
        if (!r) continue;

        // само билети
        const t = String(r.type || "").toLowerCase();
        if (t !== "ticket") continue;

        // само фискални (трябва да има УНП)
        const unp = String(r.unp || "").trim();
        if (!unp) continue;

        // трябва да има прожекция
        if (!r.show_date || !r.show_time) continue;

        const showDate = String(r.show_date).slice(0, 10); // YYYY-MM-DD
        const showTime = String(r.show_time).slice(0, 8);  // HH:mm:ss
        const movieTitle = (r.movie_title || "(без филм)").trim();
        const hallName = r.hall_name || "";

        const key = `${showDate} ${showTime} ${movieTitle} ${hallName}`;

        // дата на продажба (по фискален документ)
        const saleSource = r.doc_datetime || r.bon_datetime || null;
        const saleLabel = saleSource
          ? (fmtDeviceDayBG(saleSource) || fmtBGDay(saleSource))
          : "";

        let acc = map.get(key);
        if (!acc) {
          acc = {
            key,
            movie_title: movieTitle,
            hall_name: hallName || null,
            show_date: showDate,
            show_time: showTime,
            qty: 0,
            sum: 0,
            saleDays: new Set(), // всички дати на продажба за тази прожекция
          };
        }

        const q = safeNum(r.quantity || 1);
        const val = safeNum(r.total_price ?? r.price ?? 0);

        acc.qty += q;
        acc.sum += val;

        if (saleLabel) acc.saleDays.add(saleLabel); // напр. "05.12.2025"

        map.set(key, acc);
      }

      const rows = Array.from(map.values())
        .map((g) => ({
          ...g,
          // сортираме датите и ги залепяме със запетая
          saleDaysLabel:
            g.saleDays && g.saleDays.size
              ? Array.from(g.saleDays)
                  .sort((a, b) => a.localeCompare(b, "bg"))
                  .join(", ")
              : "",
        }))
        .sort((a, b) => {
          const as = `${a.show_date || ""}T${String(a.show_time || "00:00").slice(0, 8)}`;
          const bs = `${b.show_date || ""}T${String(b.show_time || "00:00").slice(0, 8)}`;
          if (as < bs) return -1;
          if (as > bs) return 1;
          return (a.hall_name || "").localeCompare(b.hall_name || "", "bg");
        });

      const totals = rows.reduce(
        (acc, r) => {
          acc.qty += r.qty;
          acc.sum += r.sum;
          return acc;
        },
        { qty: 0, sum: 0 }
      );

      return { rows, totals };
    }, [filteredRows]);


  

  /* ---------------------------- export CSV (details) ---------------------------- */
  const exportCsv = useCallback(() => {
    const header = [
      "type","bon_number","bon_datetime","payment_method","movie_title","hall_name",
      "show_date","show_time","seat_label","category","article_name","quantity","unit_price","total_price","user",
      "doc_no","doc_datetime","unp"   // ✅  // ← new columns at the end
    ];
    const lines = simpleData.sortedRows.map((r) =>
      [
        r.type,
        r.bon_number || "",
        dayjs(r.bon_datetime).isValid() ? dayjs(r.bon_datetime).format("YYYY-MM-DD HH:mm:ss") : "",
        r.payment_method || "",
        r.movie_title || "",
        r.hall_name || "",
        r.show_date || "",
        r.show_time || "",
        r.seat_label || "",
        r.category || "",
        r.article_name || "",
        String(r.quantity ?? 1),
        (r.unit_price ?? r.price ?? 0).toFixed(2),
        (r.total_price ?? r.price ?? 0).toFixed(2),
        r.username || "",
        r.doc_no || "",
        deviceIso(r.doc_datetime), r.unp || "",

      ].join(",")
    );
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pos-report-${toYMD(from)}_${toYMD(to)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [simpleData.sortedRows, from, to]);

  /* ---------------------------- export Excel (details) ---------------------------- */
  const exportXlsx = useCallback(() => {
    const header = [
      "type","bon_number","bon_datetime","payment_method","movie_title","hall_name",
      "show_date","show_time","seat_label","category","article_name","quantity","unit_price","total_price","user",
      "doc_no","doc_datetime","unp"
    ];
    const rowsAoa = simpleData.sortedRows.map((r) => ([
      r.type,
      r.bon_number || "",
      dayjs(r.bon_datetime).isValid() ? dayjs(r.bon_datetime).format("YYYY-MM-DD HH:mm:ss") : "",
      r.payment_method || "",
      r.movie_title || "",
      r.hall_name || "",
      r.show_date || "",
      r.show_time || "",
      r.seat_label || "",
      r.category || "",
      r.article_name || "",
      Number(r.quantity ?? 1),
      Number((r.unit_price ?? r.price ?? 0).toFixed(2)),
      Number((r.total_price ?? r.price ?? 0).toFixed(2)),
      r.username || "",
      r.doc_no || "",
      deviceIso(r.doc_datetime),
      r.unp || "",
    ]));

    const ws = XLSX.utils.aoa_to_sheet([header, ...rowsAoa]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "POS Report");
    XLSX.writeFile(wb, `pos-report-${toYMD(from)}_${toYMD(to)}.xlsx`);
  }, [simpleData.sortedRows, from, to]);




    /* --------------------------- print — SERVICE BON --------------------------- */
  // Simple helpers for 42-char fiscal lines
  const pad = (s, n) => {
    const str = String(s ?? "");
    if (str.length >= n) return str.slice(0, n);
    return str + " ".repeat(n - str.length);
  };
  const lr = (L, R, W = 42) => {
    const l = String(L ?? "");
    const r = String(R ?? "");
    const spaces = Math.max(1, W - l.length - r.length);
    return l + " ".repeat(spaces) + r;
  };
  const money = (x, at = reportAnchorDate) => `${formatMainWithSecondary(Number(x || 0), at)}`;

  // Build the lines for the service receipt (служебен бон)
  const buildServiceBonLines = (W, {
    from, to, payment, movieId, userId, hallId, dateMode,
    simpleTotals, shows, standaloneBar,
    storno
  }) => {
    const lines = [];
    lines.push("ОТЧЕТ ПО ФИЛТЪР");
    lines.push(lr(fmtBGDate(from), fmtBGDate(to), W));
    lines.push(lr("Режим дати", dateMode === "show" ? "по прожекция" : "по бон", W));
    if (payment && payment !== "all") lines.push(lr("Плащане", payment, W));
    if (movieId) lines.push(lr("Филтър: Филм", String(movieId), W));
    if (userId)  lines.push(lr("Филтър: Потребител", String(userId), W));
    if (hallId)  lines.push(lr("Филтър: Зала", String(hallId), W));
    lines.push("-".repeat(W));

    lines.push(lr("Общо",   money(simpleTotals?.sum || 0), W));
    lines.push(lr("  Билети", money(simpleTotals?.tickets || 0), W));
    lines.push(lr("  Бар",    money(simpleTotals?.bar || 0), W));
    if (simpleTotals?.quick) lines.push(lr("  Бърза",  money(simpleTotals.quick), W));
    lines.push(lr("Бройки", String(simpleTotals?.qty || 0), W));
    lines.push("-".repeat(W));

    if (Array.isArray(shows) && shows.length) {
      lines.push("ПРОЖЕКЦИИ");
      for (const g of shows) {
        const title = (g.movie_title || "(без филм)");
        const when  = `${fmtBGDay(g.show_date)} ${fmtHHmm(g.show_time)}`;
        const hall  = g.hall_name ? ` · Зала ${g.hall_name}` : "";
        const tTickets = Number(g?.tickets?.total || 0);
        const tBar     = Number(g?.bar?.total || 0);
        const grand    = tTickets + tBar;
        const qty      = Number(g?.tickets?.qty || 0) + Number(g?.bar?.qty || 0);

        lines.push(pad(title, W));
        lines.push(lr(`${when}${hall}`, money(grand), W));
        lines.push(lr("  Билети", money(tTickets), W));
        lines.push(lr("  Бар",    money(tBar), W));
        lines.push(lr("  Бройки", String(qty), W));
        lines.push("-".repeat(W));
      }
    }

    if ((standaloneBar?.sum || 0) > 0) {
      lines.push("САМОСТОЯТЕЛЕН БАР");
      lines.push(lr("  Сума",   money(standaloneBar.sum), W));
      lines.push(lr("  Бройки", String(standaloneBar.qty || 0), W));
      lines.push("-".repeat(W));
    }

    // Storno bons (separate list; not included in sales totals)
    if (Array.isArray(storno) && storno.length) {
      lines.push("СТОРНО БОНОВЕ (извън продажбите)");
      const list = storno.slice(0, 25); // keep the service bon reasonably short
      for (const s of list) {
        const bn = String(s?.bon_number || "—");
        const ref = String(s?.storno_of_unp || s?.storno_of_unp_code || s?.storno_of_bon_number || "").trim();
        lines.push(lr(pad(bn, 28), money(s?.total_amount || 0), W));
        if (ref) lines.push(pad(`на: ${ref}`, W));
      }
      if (storno.length > list.length) {
        lines.push(pad(`... +${storno.length - list.length} още`, W));
      }
      lines.push(lr("Брой", String(storno.length), W));
      lines.push("-".repeat(W));
    }

    lines.push("КРАЙ НА ОТЧЕТА");
    return lines;
  };

  const printServiceBon = useCallback(async () => {
    try {
      const W = 42;

      // strictly sort show blocks by time for pretty printing
      const shows = (showtimeData?.groups || [])
        .slice()
        .sort((a, b) => {
          const at = new Date(`${a.show_date}T${String(a.show_time || "00:00").slice(0,8)}`).getTime();
          const bt = new Date(`${b.show_date}T${String(b.show_time || "00:00").slice(0,8)}`).getTime();
          if (at !== bt) return at - bt;
          return (a.hall_name || "").localeCompare(b.hall_name || "", "bg");
        });

      const lines = buildServiceBonLines(W, {
        from, to, payment, movieId, userId, hallId, dateMode,
        simpleTotals: simpleData?.totals || { sum:0, tickets:0, bar:0, quick:0, qty:0 },
        shows,
        standaloneBar: showtimeData?.standaloneBar || { qty: 0, sum: 0 },
        storno: stornoBons || [],
      });

      // If your backend route lives elsewhere, change the URL below accordingly.
      // Try /api/fiscal/service-bon; if you get 404, use /api/pos/service-bon.
      const url = `${API_BASE}/api/fiscal/service-bon?cinema_id=${encodeURIComponent(cinemaId || "")}`;

            const token =
            localStorage.getItem("token") ||
              sessionStorage.getItem("token") ||
              null;
            const res = await fetch(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              credentials: "include",
              body: JSON.stringify({
                title: "Отчет по филтър",
                lines,
                width: W,
                // по желание: дай конкретна парола на оператор (напр. на оператор 20)
                // operator_pwd: "парола-на-оператор-20"
              }),
            });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error || "Грешка при печат");
      }
      const parts = Number(data?.parts || 1);
      alert(parts > 1
        ? `Служебният бон е изпратен към фискалното устройство. (${parts} части)`
        : "Служебният бон е изпратен към фискалното устройство."
      );
    } catch (e) {
      console.error(e);
      setError(`Печатът се провали: ${e.message}`);
    }
  }, [
    API_BASE, cinemaId,
    from, to, payment, movieId, userId, hallId, dateMode,
    simpleData, showtimeData
  ]);




  /* ----------------------------- printing (A4) ----------------------------- */
  const printA4 = () => {
    const head = `
      <meta charset="utf-8"/>
      <title>Отчети — ${fmtBGDate(from)}–${fmtBGDate(to)}</title>
      <style>
        @page { size: A4 portrait; margin: 12mm; }
        body { font: 12px/1.35 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; color:#111; }
        h1 { font-size: 18px; margin: 0 0 6px 0; }
        .muted { color:#666; margin-bottom: 8px; }
        .grid { display:grid; grid-template-columns: repeat(5,1fr); gap:8px; margin:10px 0 12px; }
        .stat { border:1px solid #ddd; border-radius:6px; padding:8px; }
        .stat .lbl { font-size:11px; color:#666; }
        .stat .val { font-weight:800; font-size:16px; }
        table { border-collapse: collapse; width: 100%; }
        thead th { text-align:left; border-bottom:1px solid #999; padding:6px 8px; white-space:nowrap; }
        tbody td { border-bottom:1px solid #eee; padding:6px 8px; white-space:nowrap; }
        tbody tr:nth-child(odd) td { background:#fafafa; }
        .right { text-align:right; }
        .mb8 { margin-bottom:8px; }
        .mt8 { margin-top:8px; }
      </style>
    `;
    const commonHeader = `
    <div class="muted">
      Период: ${fmtBGDate(from)} – ${fmtBGDate(to)}
    </div>
  `;

    const sumTable = simpleData.list.map((g) => `
      <tr>
        <td>${g.key}</td>
        <td class="right">${formatMainWithSecondary(g.sum, reportAnchorDate)}</td>
        <td class="right">${formatMainWithSecondary(g.tickets, reportAnchorDate)}</td>
        <td class="right">${formatMainWithSecondary(g.bar, reportAnchorDate)}</td>
        <td class="right">${formatMainWithSecondary(g.quick, reportAnchorDate)}</td>
        <td class="right">${g.qty}</td>
      </tr>
    `).join("");

    const breakdownHtml = showtimeData.groups.map((g, idx) => `



  <h2>${idx + 1}. ${g.movie_title} — ${fmtBGDay(g.show_date)} ${fmtHHmm(g.show_time)}${g.hall_name ? ` · Зала: ${g.hall_name}` : ""}</h2>

  <table>
    <thead><tr><th>Билети — детайл</th><th class="right">Бр.</th><th class="right">Сума</th></tr></thead>
    <tbody>
      ${g.ticketItemsArr.map(it => `
        <tr><td>${it.name}</td><td class="right">${it.qty}</td><td class="right">${formatMainWithSecondary(it.sum, groupAnchorDate(g, reportAnchorDate))}</td></tr>
      `).join("")}
      <tr><td style="font-weight:800;">Общо — Билети</td><td class="right" style="font-weight:800;">${g.tickets.qty}</td><td class="right" style="font-weight:800;">${formatMainWithSecondary(g.tickets.total, groupAnchorDate(g, reportAnchorDate))}</td></tr>
    </tbody>
  </table>

  <div class="mt8"></div>
  <div class="mb8"><b>Бар — ${formatMainWithSecondary(g.bar.total, groupAnchorDate(g, reportAnchorDate))}</b></div>
  <table>
    <thead><tr><th>Артикул</th><th class="right">Бр.</th><th class="right">Сума</th></tr></thead>
    <tbody>
      ${g.barItemsArr.map(it => `
        <tr><td>${it.name}</td><td class="right">${it.qty}</td><td class="right">${formatMainWithSecondary(it.sum, groupAnchorDate(g, reportAnchorDate))}</td></tr>
      `).join("")}
      <tr><td style="font-weight:800;">Общо — Бар</td><td class="right" style="font-weight:800;">${g.bar.qty}</td><td class="right" style="font-weight:800;">${formatMainWithSecondary(g.bar.total, groupAnchorDate(g, reportAnchorDate))}</td></tr>
    </tbody>
  </table>
`).join("<div class='mt8'></div>");

    const details = simpleData.sortedRows.map((r) => {
      const dt = r.doc_datetime
      ? fmtDeviceDateTimeBG(r.doc_datetime)
      : (r.show_date ? `${fmtBGDay(r.show_date)} ${r.show_time || ""}` : "");
    
      const seat = r.seat_label ? `${r.seat_label}${r.category ? ` (${r.category})` : ""}` : "";
      const icon = isQuickSaleRow(r) ? "⚡" : (r.type === "ticket" ? "🎫" : "🥤");
      return `
        <tr>
          <td>${icon}</td>
          <td>${dt || "—"}</td>
          <td>${r.bon_number || "—"}</td>
          <td>${r.payment_method || "—"}</td>
          <td>${r.movie_title || "—"}</td>
          <td>${r.hall_name || "—"}</td>
          <td>${fmtBGDay(r.show_date) || "—"} ${fmtHHmm(r.show_time) || ""}</td>
          <td>${seat || "—"}</td>
          <td>${r.article_name || (r.type === "ticket" ? "Билет" : "—")}</td>
          <td class="right">${String(r.quantity ?? 1)}</td>
          <td class="right">${formatMainWithSecondary((r.unit_price ?? r.price ?? 0), rowSaleDate(r) || reportAnchorDate)}</td>
          <td class="right">${formatMainWithSecondary((r.total_price ?? r.price ?? 0), rowSaleDate(r) || reportAnchorDate)}</td>
          <td>${r.username || "—"}</td>
        </tr>
      `;
    }).join("");

    const stornoHtml = stornoBons.length ? `
      <div class="mt8"></div>
      <h2>Сторно бонове (извън дневните продажби)</h2>
      <table>
        <thead>
          <tr>
            <th>Дата/час</th>
            <th>Фискален №</th>
            <th class="right">Сума</th>
            <th>Плащане</th>
            <th>Потребител</th>
            <th>Сторно на</th>
          </tr>
        </thead>
        <tbody>
          ${stornoBons.map(s => `
            <tr>
              <td>${fmtBGDateTime(s.bon_datetime)}</td>
              <td>${s.bon_number}</td>
              <td class="right">${formatMainWithSecondary(Number(s.total_amount || 0), rowSaleDate(s) || reportAnchorDate)}</td>
              <td>${(s.payment_method || "").toLowerCase()}</td>
              <td>${s.username || "—"}</td>
              <td>${s.storno_of_unp || s.storno_of_bon_id || "—"}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    ` : "";

    const html = `
      <html>
        <head>${head}</head>
        <body>
          <h1>POS отчет</h1>
          ${commonHeader}

          <div class="grid">
            <div class="stat"><div class="lbl">Общо</div><div class="val">${formatMainWithSecondary(simpleData.totals.sum, reportAnchorDate)}</div></div>
            <div class="stat"><div class="lbl">Билети</div><div class="val">${formatMainWithSecondary(simpleData.totals.tickets, reportAnchorDate)}</div></div>
            <div class="stat"><div class="lbl">Бар</div><div class="val">${formatMainWithSecondary(simpleData.totals.bar, reportAnchorDate)}</div></div>
            <div class="stat"><div class="lbl">Бърза продажба</div><div class="val">${formatMainWithSecondary(simpleData.totals.quick, reportAnchorDate)}</div></div>
            <div class="stat"><div class="lbl">Бройки</div><div class="val">${String(simpleData.totals.qty)}</div></div>
          </div>

          <div class="mb8"><b>Обобщение</b></div>
          <table>
            <thead>
              <tr>
                <th>Група</th>
                <th class="right">Сума</th>
                <th class="right">Билети</th>
                <th class="right">Бар</th>
                <th class="right">Бърза</th>
                <th class="right">Бр.</th>
              </tr>
            </thead>
            <tbody>${sumTable}</tbody>
          </table>

          <div class="mt8"></div>
          ${breakdownHtml}

          ${stornoHtml}

          <div class="mt8"></div>
          <div class="mb8"><b>Детайлни редове</b></div>
          <table>
            <thead>
              <tr>
                <th>Тип</th>
                <th>Дата/час</th>
                <th>Фискален №</th>
                <th>Плащане</th>
                <th>Филм</th>
                <th>Зала</th>
                <th>Прожекция</th>
                <th>Място</th>
                <th>Артикул/Бележка</th>
                <th class="right">Бр.</th>
                <th class="right">Ед. цена</th>
                <th class="right">Общо</th>
                <th>Потребител</th>
              </tr>
            </thead>
            <tbody>${details}</tbody>
          </table>
        </body>
      </html>
    `;

    const w = window.open("", "_blank");
    if (w) {
      w.document.write(html);
      w.document.close();
      w.focus();
      w.print();
    }
  };

  /* ----------------------------------- UI ----------------------------------- */
  const padCell = "8px 10px";
  const thPad = "8px 10px";
  const isSmDown = false;

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const stornoTotals = useMemo(() => {
    const sum = (stornoBons || []).reduce((a, s) => a + safeNum(s.total_amount), 0);
    return { count: stornoBons?.length || 0, sum };
  }, [stornoBons]);



  // брояч за промо билети (0.00 или маркирани като sale_kind='promo')
  const promoTicketsCount = useMemo(() => {
    return filteredRows.filter((r) => isPromoTicketRow(r)).length;
  }, [filteredRows]);


  // Printed fiscal bons list (includes bons that were later stornoed).
  // This is separate from the sales totals (which exclude stornoed originals).
  const printedBonsList = useMemo(() => {
    const m = new Map();
    for (const r of (basicRowsAll || [])) {
      if (!r) continue;
      const unp = String(r.unp || r.bon_number || "").trim();
      if (!unp) continue;

      const cur = m.get(unp) || {
        unp,
        bon_number: String(r.bon_number || "").trim(),
        bon_datetime: r.bon_datetime || null,
        doc_datetime: r.doc_datetime || null,
        payment_method: r.payment_method || "",
        username: r.username || "",
        sum: 0,
        tickets: 0,
        bar: 0,
        quick: 0,
      };

      // Prefer the fiscal device time if present
      if (!cur.doc_datetime && r.doc_datetime) cur.doc_datetime = r.doc_datetime;
      if (!cur.bon_datetime && r.bon_datetime) cur.bon_datetime = r.bon_datetime;
      if (!cur.payment_method && r.payment_method) cur.payment_method = r.payment_method;
      if (!cur.username && r.username) cur.username = r.username;
      if (!cur.bon_number && r.bon_number) cur.bon_number = r.bon_number;

      const val = safeNum(r.total_price ?? r.price ?? 0);
      cur.sum += val;

      const t = String(r.type || "").toLowerCase();
      if (isQuickSaleRow(r)) cur.quick += val;
      else if (t === "ticket") cur.tickets += val;
      else if (t === "bar") cur.bar += val;

      m.set(unp, cur);
    }

    const out = Array.from(m.values()).map((b) => {
      const stornoArr = stornoByUnp.get(b.unp) || [];
      const stornoAbs = stornoSets.stornoAbsByUnp.get(String(b.unp || "").trim()) || 0;
      const isCanceled = stornoSets.fullCanceledByUnp.has(b.unp);
      const isPartial = !isCanceled && stornoAbs > 0;
      const netSum = +(Number(b.sum || 0) - Number(stornoAbs || 0)).toFixed(2);
      return {
        ...b,
        isCanceled,
        isPartial,
        storno_abs: stornoAbs,
        net_sum: netSum,
        storno_numbers: stornoArr.map((s) => String(s?.bon_number || "").trim()).filter(Boolean),
      };
    });

    // Sort by fiscal doc time (desc), then bon time
    out.sort((a, b) => {
      const ad = deviceMsUTC(a.doc_datetime) || (a.bon_datetime ? new Date(a.bon_datetime).getTime() : 0);
      const bd = deviceMsUTC(b.doc_datetime) || (b.bon_datetime ? new Date(b.bon_datetime).getTime() : 0);
      return bd - ad;
    });
    return out;
  }, [basicRowsAll, stornoByUnp, stornoSets]);

// Storno rows that cannot be matched to a printed bon (by storno_of_unp)
const stornoUnmatched = useMemo(() => {
  const printed = new Set((printedBonsList || []).map(b => String(b?.unp || "").trim()).filter(Boolean));
  return (stornoBons || []).filter(s => {
    const ref = String(
      s?.storno_of_unp || s?.storno_of_unp_code || s?.storno_of_bon_number || s?.storno_of_bon_id || ""
    ).trim();
    if (!ref) return true;
    return !printed.has(ref);
  });
}, [stornoBons, printedBonsList]);




  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="bg">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, color: "#111", background: "#f5f7fb", p: { xs: 1, md: 1.5 }, borderRadius: 2 }}>
        {/* HEADER */}
        <Paper sx={{ p: 1, bgcolor: "#fff", borderRadius: 3, border: "1px solid #e8e8f3", position: "sticky", top: 8, zIndex: 5 }}>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={1} alignItems={{ lg: "center" }} justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <DatePicker label="От" value={from} onChange={(v) => setFrom(v)} slotProps={{ textField: { size: "small" } }} />
              <DatePicker label="До" value={to} onChange={(v) => setTo(v)} slotProps={{ textField: { size: "small" } }} />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, flexWrap: "wrap" }}>
              <Tooltip title="Обнови"><span><Button variant="outlined" size="small" onClick={loadData} disabled={loading}>Обнови</Button></span></Tooltip>
              <Tooltip title="Печат (A4)"><span><IconButton onClick={printA4} disabled={loading}><PrintIcon /></IconButton></span></Tooltip>
              <Tooltip title="Експорт CSV (детайли)"><span><IconButton onClick={exportCsv} disabled={loading}><DownloadIcon /></IconButton></span></Tooltip>
              <Tooltip title="Експорт Excel (.xlsx)"><span><IconButton onClick={exportXlsx} disabled={loading}><DownloadIcon /></IconButton></span></Tooltip>
                 <Tooltip title="Печат — служебен бон (фискално устройство)">
                    <span><IconButton onClick={printServiceBon} disabled={loading}><ReceiptLongIcon /></IconButton></span>
                  </Tooltip>
            
            
            
              </Stack>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: "wrap" }}>
            <TextField
              select
              size="small"
              label="Вид продажба"
              value={reportKind}
              onChange={(e) => setReportKind(e.target.value)}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="all">Всички</MenuItem>
              <MenuItem value="tickets">Само билети (филми)</MenuItem>
              <MenuItem value="bar">Само бар</MenuItem>
              <MenuItem value="mixed_bons">Бон: бар + билети</MenuItem>
              <MenuItem value="ticket_only_bons">Бон: само билети</MenuItem>
              <MenuItem value="bar_only_bons">Бон: само бар</MenuItem>
              <MenuItem value="quick">Само бързи продажби</MenuItem>
              <MenuItem value="quick_only_bons">Бон: само бързи</MenuItem>
              <MenuItem value="promo_tickets">Само промо/безплатни билети</MenuItem>
              <MenuItem value="paid_tickets">Само платени билети</MenuItem>
              <MenuItem value="bar_with_show">Бар към прожекция</MenuItem>
              <MenuItem value="bar_standalone">Бар без прожекция</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="Плащане"
              value={payment}
              onChange={(e) => setPayment(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="all">Всички</MenuItem>
              <MenuItem value="cash">Кеш</MenuItem>
              <MenuItem value="card">Карта</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="Филм"
              value={movieId ? String(movieId) : ""}
              onChange={(e) => { setMovieId(e.target.value); setSearch(""); }}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="">Всички</MenuItem>
              {movieOptions.filter((m) => !String(m.value).startsWith("title::")).map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.title || "(без филм)"}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Касиер"
              value={userId ? String(userId) : ""}
              onChange={(e) => setUserId(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Всички</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.user_id} value={String(u.user_id)}>{u.username}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Зала"
              value={hallId ? String(hallId) : ""}
              onChange={(e) => setHallId(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Всички</MenuItem>
              {halls.map((h) => (
                <MenuItem key={h.HALL_ID ?? h.hall_id ?? h.id} value={String(h.HALL_ID ?? h.hall_id ?? h.id)}>
                  {h.HALL_NAME ?? h.hall_name ?? h.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Търсене"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220 }}
            />

            <TextField
              select
              size="small"
              label="Групиране"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="day">По ден</MenuItem>
              <MenuItem value="movie">По филм</MenuItem>
              <MenuItem value="showtime">По прожекция</MenuItem>
              <MenuItem value="user">По потребител</MenuItem>
              <MenuItem value="none">Без</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="Сортиране"
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
              sx={{ minWidth: 190 }}
            >
              {Object.entries(SORT_FIELDS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </TextField>

            <Button
              variant="text"
              size="small"
              onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            >
              {sortDir === "asc" ? "↑" : "↓"}
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setReportKind("all");
                setPayment("all");
                setMovieId("");
                setUserId("");
                setHallId("");
                setSearch("");
                setGroupBy("day");
                setSortField("datetime");
                setSortDir("asc");
              }}
            >
              Изчисти
            </Button>
          </Stack>

        </Paper>

        {/* TABS: режим на отчета */}
        <Paper
          sx={{
            mt: 0.5,
            mb: 0.5,
            p: 0.5,
            bgcolor: "#fff",
            borderRadius: 3,
            border: "1px solid #e8e8f3",
          }}
        >
          <Tabs
            value={viewTab}
            onChange={(_, v) => setViewTab(v)}
            variant="fullWidth"
          >
            <Tab value="sale" label="По дата на продажба" />
            <Tab value="showtime" label="По прожекция" />
          </Tabs>
        </Paper>


        
        {error ? <Alert severity="error">{error}</Alert> : null}
        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={20} /><Typography>Зареждане...</Typography>
          </Stack>
        ) : null}

        {/* SUMMARY GRID */}
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          <Stat label="Общо" value={`${formatMainWithSecondary(simpleData.totals.sum, reportAnchorDate)}`} accent />
          <Stat label="Билети" value={`${formatMainWithSecondary(simpleData.totals.tickets, reportAnchorDate)}`} />
          <Stat label="Бар" value={`${formatMainWithSecondary(simpleData.totals.bar, reportAnchorDate)}`} />
          <Stat label="Промо билети" value={String(promoTicketsCount)} />
          <Stat label="Бърза продажба" value={`${formatMainWithSecondary(simpleData.totals.quick, reportAnchorDate)}`} />
          <Stat label="Бройки" value={String(simpleData.totals.qty)} />
        </Stack>


{/* Flat list: ALL showtimes, strictly sorted by time (incl. standalone bar) */}
{(showtimeData?.groups?.length || 0) > 0 && (
  <Box sx={{ mb: 1.5, p: 1, border: "1px solid #e8e8f3", borderRadius: 2, bgcolor: "#fff" }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
      Прожекции — всички филми (сортирано по час)
    </Typography>

    {(() => {
      const flatSorted = showtimeData.groups
        .slice()
        .sort((a, b) => {
          const at = new Date(`${a.show_date}T${String(a.show_time).slice(0, 8)}`).getTime();
          const bt = new Date(`${b.show_date}T${String(b.show_time).slice(0, 8)}`).getTime();
          if (at !== bt) return at - bt;
          return (a.hall_name || "").localeCompare(b.hall_name || "", "bg");
        });

      // base totals from showtimes
      const totals = flatSorted.reduce(
        (acc, g) => {
          acc.tickets += g?.tickets?.total || 0;
          acc.bar     += g?.bar?.total     || 0;
          acc.qty     += (g?.tickets?.qty || 0) + (g?.bar?.qty || 0);
          return acc;
        },
        { tickets: 0, bar: 0, qty: 0 }
      );

      // add standalone bar to totals
      const sbQty = showtimeData?.standaloneBar?.qty || 0;
      const sbSum = showtimeData?.standaloneBar?.sum || 0;
      totals.bar += sbSum;
      totals.qty += sbQty;

      const grand = totals.tickets + totals.bar;

      return (
        <TableShell bg="#F6F3FF" borderColor="#DCD6FF">
          <colgroup>
            <col style={{ width: "1%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "30%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "8%" }} />
          </colgroup>

          <thead style={{ background: "#EDE9FE" /* a shade darker than bg */ }}>
          <tr>
              <Th>#</Th>
              <Th>Дата</Th>
              <Th>Филм</Th>
              <Th>Час</Th>
              <Th>Зала</Th>
              <ThRight>Билети</ThRight>
              <ThRight>Бар</ThRight>
              <ThRight>Общо</ThRight>
              <ThRight>Бр.</ThRight>
            </tr>
          </thead>

          <tbody>
  {flatSorted.map((g, idx) => {
    const showKey = g.key || `${g.movie_title}-${g.show_date}-${g.show_time}-${g.hall_name || ""}-${idx}`;
    const openShow = isShowOpen(showKey);

    const tTickets = g?.tickets?.total || 0;
    const tBar     = g?.bar?.total     || 0;
    const tGrand   = tTickets + tBar;
    const tQty     = (g?.tickets?.qty || 0) + (g?.bar?.qty || 0);

    return (
      <React.Fragment key={showKey}>
        <tr
          onClick={() => toggleShowOpen(showKey)}
          style={{ cursor: "pointer" }}
          title={openShow ? "Скрий детайли" : "Покажи детайли"}
        >
          <Td>{idx + 1}</Td>
          <Td>{fmtBGDay(g.show_date)}</Td>
          <Td>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <span>{g.movie_title}</span>
              <Caret open={openShow} />
            </Stack>
          </Td>
          <Td>{fmtHHmm(g.show_time)}</Td>
          <Td>{g.hall_name || "—"}</Td>
          <TdRight>{formatMainWithSecondary(tTickets, groupAnchorDate(g, reportAnchorDate))}</TdRight>
          <TdRight>{formatMainWithSecondary(tBar, groupAnchorDate(g, reportAnchorDate))}</TdRight>
          <TdRight>{formatMainWithSecondary(tGrand, groupAnchorDate(g, reportAnchorDate))}</TdRight>
          <TdRight>{String(tQty)}</TdRight>
        </tr>

        {openShow && (
          <tr>
            <td colSpan={9} style={{ padding: 0, background: "#F6F3FF" }}>
              <div style={{ padding: "10px" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                  {idx + 1}. {g.movie_title} · {fmtBGDay(g.show_date)} {fmtHHmm(g.show_time)}
                  {g.hall_name ? ` · Зала: ${g.hall_name}` : ""}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mb: 1 }}>
                  <Stat label="Билети — общо" value={`${formatMainWithSecondary((g.tickets.total || 0), groupAnchorDate(g, reportAnchorDate))}`} />
                  <Stat label="Бар — общо"     value={`${formatMainWithSecondary((g.bar.total || 0), groupAnchorDate(g, reportAnchorDate))}`} />
                  <Stat label="Продажби — общо" value={`${formatMainWithSecondary(((g.tickets.total || 0) + (g.bar.total || 0)), groupAnchorDate(g, reportAnchorDate))}`} accent />
                  <Stat label="Бройки"         value={String((g.tickets.qty || 0) + (g.bar.qty || 0))} />
                </Stack>

                {/* Tickets */}
                {!!(g.ticketItemsArr || []).length && (
                  <Box sx={{ mt: 0.25, mb: 1 }}>
                    <TableShell>
                      <colgroup>
                        <col style={{ width: "50%" }} />
                        <col />
                        <col />
                        <col />
                      </colgroup>
                      <thead>
                        <tr>
                          <Th>Тип</Th>
                          <ThRight>Бр.</ThRight>
                          <ThRight>Ед. цена</ThRight>
                          <ThRight>Сума</ThRight>
                        </tr>
                      </thead>
                      <tbody>
                        {g.ticketItemsArr.map((it) => {
                          const key = `${g.key}|ticket|${it.name}`;
                          const open = isOpen(key);
                          const details = g.ticketDetails?.get(it.name) || [];
                          const unit = it.qty ? (it.sum / it.qty) : 0;
                          const anchor = groupAnchorDate(g, reportAnchorDate);

                          return (
                            <React.Fragment key={key}>
                              <tr
                                onClick={(e) => { e.stopPropagation(); if (details.length) toggleOpen(key); }}
                                style={{ cursor: details.length ? "pointer" : "default" }}
                                title={details.length ? "Покажи/скрий детайли" : ""}
                              >
                                <Td>
                                  {it.name} {details.length ? <Caret open={open} /> : null}
                                </Td>
                                <TdRight>{String(it.qty)}</TdRight>
                                <TdRight>{formatMainWithSecondary(unit, anchor)}</TdRight>
                                <TdRight>{formatMainWithSecondary(it.sum, anchor)}</TdRight>
                              </tr>

                              {open && details.length > 0 && (
                                <tr>
                                  <td colSpan={4} style={{ padding: 0, background: "#fafafa" }}>
                                    <div style={{ padding: "6px 8px" }}>
                                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                          <tr>
                                            <Th>Фискален №</Th>
                                            <Th>Дата/час (фиск.)</Th>
                                            <Th>Док. №</Th>
                                            <Th>Място</Th>
                                            <ThRight>Цена</ThRight>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {details
                                            .sort((a,b) => String(a.doc_datetime||"").localeCompare(String(b.doc_datetime||"")))
                                            .map((d, i) => (
                                              <tr key={i}>
                                                <Td>{d.bon_number || "—"}</Td>
                                                <Td>{d.doc_datetime ? fmtDeviceDateTimeBG(d.doc_datetime) : "—"}</Td>
                                                <Td>{d.doc_no || "—"}</Td>
                                                <Td>{d.seat_label || "—"}</Td>
                                                <TdRight>{formatMainWithSecondary(Number(d.unit_price ?? d.price ?? 0), rowSaleDate(d) || groupAnchorDate(g, reportAnchorDate))}</TdRight>
                                              </tr>
                                            ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}

                        <tr aria-hidden="true">
                          <td colSpan={4} style={{ padding: 0, borderBottom: 0 }}>
                            <div style={{ borderTop: "2px solid #999" }} />
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr>
                          <Td style={{ fontWeight: 800, borderTop: 0 }}>Общо — Билети</Td>
                          <TdRight style={{ fontWeight: 800, borderTop: 0 }}>{String(g.tickets.qty)}</TdRight>
                          <TdRight style={{ borderTop: 0 }} />
                          <TdRight style={{ fontWeight: 800, borderTop: 0 }}>{formatMainWithSecondary(g.tickets.total, groupAnchorDate(g, reportAnchorDate))}</TdRight>
                        </tr>
                      </tfoot>
                    </TableShell>
                  </Box>
                )}

                {/* Bar */}
                {!!(g.barItemsArr || []).length && (
                  <Box sx={{ mt: 0.25 }}>
                    <TableShell>
                      <colgroup>
                        <col style={{ width: "50%" }} />
                        <col />
                        <col />
                        <col />
                      </colgroup>
                      <thead>
                        <tr>
                          <Th>Артикул</Th>
                          <ThRight>Бр.</ThRight>
                          <ThRight>Ед. цена</ThRight>
                          <ThRight>Сума</ThRight>
                        </tr>
                      </thead>
                      <tbody>
                        {g.barItemsArr.map((it) => {
                          const key = `${g.key}|bar|${it.name}`;
                          const open = isOpen(key);
                          const details = g.barDetails?.get(it.name) || [];
                          const unit = it.qty ? (it.sum / it.qty) : 0;
                          const anchor = groupAnchorDate(g, reportAnchorDate);

                          return (
                            <React.Fragment key={key}>
                              <tr
                                onClick={(e) => { e.stopPropagation(); if (details.length) toggleOpen(key); }}
                                style={{ cursor: details.length ? "pointer" : "default" }}
                                title={details.length ? "Покажи/скрий детайли" : ""}
                              >
                                <Td>
                                  {it.name} {details.length ? <Caret open={open} /> : null}
                                </Td>
                                <TdRight>{String(it.qty)}</TdRight>
                                <TdRight>{formatMainWithSecondary(unit, anchor)}</TdRight>
                                <TdRight>{formatMainWithSecondary(it.sum, anchor)}</TdRight>
                              </tr>

                              {open && details.length > 0 && (
                                <tr>
                                  <td colSpan={4} style={{ padding: 0, background: "#fafafa" }}>
                                    <div style={{ padding: "6px 8px" }}>
                                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                          <tr>
                                            <Th>Фискален №</Th>
                                            <Th>Дата/час (фиск.)</Th>
                                            <Th>Док. №</Th>
                                            <ThRight>Цена</ThRight>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {details
                                            .sort((a,b) => String(a.doc_datetime||"").localeCompare(String(b.doc_datetime||"")))
                                            .map((d, i) => (
                                              <tr key={i}>
                                                <Td>{d.bon_number || "—"}</Td>
                                                <Td>{d.doc_datetime ? fmtDeviceDateTimeBG(d.doc_datetime) : "—"}</Td>
                                                <Td>{d.doc_no || "—"}</Td>
                                                <TdRight>{formatMainWithSecondary(Number(d.unit_price ?? d.price ?? d.sum ?? 0), rowSaleDate(d) || groupAnchorDate(g, reportAnchorDate))}</TdRight>
                                              </tr>
                                            ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}

                        <tr aria-hidden="true">
                          <td colSpan={4} style={{ padding: 0, borderBottom: 0 }}>
                            <div style={{ borderTop: "2px solid #999" }} />
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr>
                          <Td style={{ fontWeight: 800, borderTop: 0 }}>Общо — Бар</Td>
                          <TdRight style={{ fontWeight: 800, borderTop: 0 }}>{String(g.bar.qty)}</TdRight>
                          <TdRight style={{ borderTop: 0 }} />
                          <TdRight style={{ fontWeight: 800, borderTop: 0 }}>{formatMainWithSecondary(g.bar.total, groupAnchorDate(g, reportAnchorDate))}</TdRight>
                        </tr>
                      </tfoot>
                    </TableShell>
                  </Box>
                )}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  })}

  {/* Optional row for standalone bar (no showtime) */}
  {sbSum > 0 && (() => {
    const key = "__standalone_bar__";
    const open = isShowOpen(key);
    return (
      <React.Fragment key={key}>
        <tr
          onClick={() => toggleShowOpen(key)}
          style={{ cursor: "pointer" }}
          title={open ? "Скрий детайли" : "Покажи детайли"}
        >
          <Td>—</Td>
          <Td>—</Td>
          <Td>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <i>Самостоятелен бар (без прожекция)</i>
              <Caret open={open} />
            </Stack>
          </Td>
          <Td>—</Td>
          <Td>—</Td>
          <TdRight>{formatMainWithSecondary(0, reportAnchorDate)}</TdRight>
          <TdRight>{formatMainWithSecondary(sbSum, reportAnchorDate)}</TdRight>
          <TdRight>{formatMainWithSecondary(sbSum, reportAnchorDate)}</TdRight>
          <TdRight>{String(sbQty)}</TdRight>
        </tr>

        {open && (
          <tr>
            <td colSpan={9} style={{ padding: 0, background: "#F6F3FF" }}>
              <div style={{ padding: "10px" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Самостоятелен бар (без прожекция)
                </Typography>

                <TableShell>
                  <colgroup>
                    <col style={{ width: "32%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "12%" }} />
                  </colgroup>

                  <thead>
                    <tr>
                      <Th>Артикул</Th>
                      <ThCenter>УНП</ThCenter>
                      <ThCenter>Дата</ThCenter>
                      <ThCenter>Час</ThCenter>
                      <ThCenter>Док. №</ThCenter>
                      <ThRight>Бр.</ThRight>
                      <ThRight>Сума</ThRight>
                    </tr>
                  </thead>

                  <tbody>
                    {(showtimeData?.standaloneBarItemsArr || []).map((it) => (
                      <tr key={`${it.date}|${it.time}|${it.doc_no}|${it.name}`}>
                        <Td>{it.name}</Td>
                        <TdCenter>{it.unp || "—"}</TdCenter>
                        <TdCenter>{it.date || "—"}</TdCenter>
                        <TdCenter>{it.time || "—"}</TdCenter>
                        <TdCenter>{it.doc_no || "—"}</TdCenter>
                        <TdRight>{String(it.qty)}</TdRight>
                        <TdRight>{formatMainWithSecondary(it.sum, reportAnchorDate)}</TdRight>
                      </tr>
                    ))}

                    <tr aria-hidden="true">
                      <td colSpan={7} style={{ padding: 0, borderBottom: 0 }}>
                        <div style={{ borderTop: "2px solid #999" }} />
                      </td>
                    </tr>
                  </tbody>

                  <tfoot>
                    <tr>
                      <Td style={{ fontWeight: 800, borderTop: 0 }}>Общо — Самостоятелен бар</Td>
                      <TdCenter style={{ borderTop: 0 }} />
                      <TdCenter style={{ borderTop: 0 }} />
                      <TdCenter style={{ borderTop: 0 }} />
                      <TdCenter style={{ borderTop: 0 }} />
                      <TdRight style={{ fontWeight: 800, borderTop: 0 }}>{String(showtimeData?.standaloneBar?.qty || 0)}</TdRight>
                      <TdRight style={{ fontWeight: 800, borderTop: 0 }}>{formatMainWithSecondary(showtimeData?.standaloneBar?.sum || 0, reportAnchorDate)}</TdRight>
                    </tr>
                  </tfoot>
                </TableShell>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  })()}
</tbody>


            {/* separator line before totals */}
            <tr aria-hidden="true">
              <td colSpan={9} style={{ padding: 0, borderBottom: 0 }}>
                <div style={{ borderTop: "2px solid #999" }} />
              </td>
            </tr>
  
            <tfoot>
              <tr>
                <Td colSpan={5} style={{ fontWeight: 800 }}>Общо</Td>
                <TdRight style={{ fontWeight: 800 }}>{formatMainWithSecondary(totals.tickets, reportAnchorDate)}</TdRight>
                <TdRight style={{ fontWeight: 800 }}>{formatMainWithSecondary(totals.bar, reportAnchorDate)}</TdRight>
                <TdRight style={{ fontWeight: 800 }}>{formatMainWithSecondary(grand, reportAnchorDate)}</TdRight>
                <TdRight style={{ fontWeight: 800 }}>{String(totals.qty)}</TdRight>
              </tr>
            </tfoot>
  
        </TableShell>
      );
    })()}
  </Box>
)}



        



{/* Bar sales summary (by article) – includes standalone + bar-with-tickets */}
{barSalesSummary.items.length > 0 && (
  <Box sx={{ mb: 1.5, p: 1, border: "1px solid #BFE6C7", borderRadius: 2, bgcolor: "#F3FFF3" }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
      Бар продажби — обобщение по артикули
    </Typography>

    <TableShell bg="#F3FFF3" borderColor="#BFE6C7">
      <colgroup>
        <col style={{ width: "28%" }} />  {/* Артикул */}
        <col style={{ width: "10%" }} />  {/* Самост. бр */}
        <col style={{ width: "12%" }} />  {/* Самост. сума */}
        <col style={{ width: "10%" }} />  {/* С билети бр */}
        <col style={{ width: "12%" }} />  {/* С билети сума */}
        <col style={{ width: "10%" }} />  {/* Общо бр */}
        <col style={{ width: "18%" }} />  {/* Общо сума */}
      </colgroup>

      <thead>
        <tr>
          <Th>Артикул</Th>
          <ThRight>Самост. бр.</ThRight>
          <ThRight>Самост. сума</ThRight>
          <ThRight>С билети бр.</ThRight>
          <ThRight>С билети сума</ThRight>
          <ThRight>Общо бр.</ThRight>
          <ThRight>Общо</ThRight>
        </tr>
      </thead>

      <tbody>
        {barSalesSummary.items.map((it) => {
          const key = `barsum|${it.name}`;
          const open = isOpen(key);
          const details = barSalesSummary.details.get(it.name) || [];

          return (
            <React.Fragment key={key}>
              <tr
                onClick={() => toggleOpen(key)}
                style={{ cursor: details.length ? "pointer" : "default" }}
                title={details.length ? "Покажи/скрий детайли" : ""}
              >
                <Td>
                  {it.name} {details.length ? <Caret open={open} /> : null}
                </Td>
                <TdRight>{String(it.standaloneQty)}</TdRight>
                <TdRight>{formatMainWithSecondary(Number(it.standaloneSum || 0), reportAnchorDate)}</TdRight>
                <TdRight>{String(it.withTicketQty)}</TdRight>
                <TdRight>{formatMainWithSecondary(Number(it.withTicketSum || 0), reportAnchorDate)}</TdRight>
                <TdRight>{String(it.qty)}</TdRight>
                <TdRight>{formatMainWithSecondary(Number(it.sum || 0), reportAnchorDate)}</TdRight>
              </tr>

              {open && details.length > 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 0, background: "#fafafa" }}>
                    <div style={{ padding: "6px 8px" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <Th>Тип</Th>
                            <Th>Фискален №</Th>
                            <ThCenter>УНП</ThCenter>
                            <Th>Дата/час (фиск.)</Th>
                            <Th>Док. №</Th>
                            <Th>Касиер</Th>
                            <ThRight>Бр.</ThRight>
                            <ThRight>Цена</ThRight>
                            <ThRight>Сума</ThRight>
                          </tr>
                        </thead>
                        <tbody>
                          {details
                            .sort((a, b) => deviceMsUTC(a.doc_datetime) - deviceMsUTC(b.doc_datetime))
                            .map((d, i) => (
                              <tr key={i}>
                                <Td>{d.kind === "bar_with_ticket" ? "Бар + билет" : "Самостоятелен"}</Td>
                                <Td>{d.bon_number || "—"}</Td>
                                <TdCenter>{d.unp || "—"}</TdCenter>
                                <Td>{d.doc_datetime ? fmtDeviceDateTimeBG(d.doc_datetime) : "—"}</Td>
                                <Td>{d.doc_no || "—"}</Td>
                                <Td>{d.username || "—"}</Td>
                                <TdRight>{String(d.qty)}</TdRight>
                                <TdRight>{formatMainWithSecondary(Number(d.unit_price ?? 0), rowSaleDate(d) || reportAnchorDate)}</TdRight>
                                <TdRight>{formatMainWithSecondary(Number(d.sum ?? 0), rowSaleDate(d) || reportAnchorDate)}</TdRight>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}

        {/* separator before totals */}
        <tr aria-hidden="true">
          <td colSpan={7} style={{ padding: 0, borderBottom: 0 }}>
            <div style={{ borderTop: "2px solid #999" }} />
          </td>
        </tr>
      </tbody>

      <tfoot>
        <tr>
          <Td style={{ fontWeight: 800, borderTop: 0 }}>Общо — Бар (за периода)</Td>
          <TdRight style={{ fontWeight: 800, borderTop: 0 }}>{String(barSalesSummary.totals.standaloneQty)}</TdRight>
          <TdRight style={{ fontWeight: 800, borderTop: 0 }}>{formatMainWithSecondary(Number(barSalesSummary.totals.standaloneSum), reportAnchorDate)}</TdRight>
          <TdRight style={{ fontWeight: 800, borderTop: 0 }}>{String(barSalesSummary.totals.withTicketQty)}</TdRight>
          <TdRight style={{ fontWeight: 800, borderTop: 0 }}>{formatMainWithSecondary(Number(barSalesSummary.totals.withTicketSum), reportAnchorDate)}</TdRight>
          <TdRight style={{ fontWeight: 800, borderTop: 0 }}>{String(barSalesSummary.totals.qty)}</TdRight>
          <TdRight style={{ fontWeight: 800, borderTop: 0 }}>{formatMainWithSecondary(Number(barSalesSummary.totals.sum), reportAnchorDate)}</TdRight>
        </tr>
      </tfoot>
    </TableShell>
  </Box>
)}


            {/* Quick sales (no showtime) */}
            {quickData.items.length > 0 && (
              <Box sx={{ mb: 1.5, p: 1, border: "1px solid #e8e8f3", borderRadius: 2, bgcolor: "#fff" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Бързи продажби (Quick sale)
                </Typography>
      
                <TableShell>
                  <colgroup>
                    <col style={{ width: "50%" }} />  {/* Артикул */}
                    <col style={{ width: "15%" }} />  {/* Бр. */}
                    <col style={{ width: "15%" }} />  {/* Ед. цена */}
                    <col style={{ width: "20%" }} />  {/* Сума */}
                  </colgroup>
      
                  <thead>
                    <tr>
                      <Th>Артикул</Th>
                      <ThRight>Бр.</ThRight>
                      <ThRight>Ед. цена</ThRight>
                      <ThRight>Сума</ThRight>
                    </tr>
                  </thead>
      
                  <tbody>
                    {quickData.items.map((it) => {
                      const key = `quick|${it.name}`;
                      const open = isOpen(key);
                      const details = quickData.details.get(it.name) || [];
                      const unit = it.qty ? (it.sum / it.qty) : 0;
                      const anchor = reportAnchorDate;
      
                      return (
                        <React.Fragment key={key}>
                          <tr
                            onClick={() => toggleOpen(key)}
                            style={{ cursor: details.length ? "pointer" : "default" }}
                            title={details.length ? "Покажи/скрий детайли" : ""}
                          >
                            <Td>
                              {it.name} {details.length ? <Caret open={open} /> : null}
                            </Td>
                            <TdRight>{String(it.qty)}</TdRight>
                            <TdRight>{formatMainWithSecondary(unit, anchor)}</TdRight>
                            <TdRight>{formatMainWithSecondary(it.sum, anchor)}</TdRight>
                          </tr>
      
                          {open && details.length > 0 && (
                            <tr>
                              <td colSpan={4} style={{ padding: 0, background: "#fafafa" }}>
                                <div style={{ padding: "6px 8px" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                      <tr>
                                        <Th>Фискален №</Th>
                                        <ThCenter>УНП</ThCenter>
                                        <Th>Дата/час (фиск.)</Th>
                                        <Th>Док. №</Th>
                                        <ThRight>Бр.</ThRight>
                                        <ThRight>Ед. цена</ThRight>
                                        <ThRight>Сума</ThRight>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {details
                                        .sort((a, b) =>
                                          String(a.doc_datetime || "").localeCompare(String(b.doc_datetime || ""))
                                        )
                                        .map((d, i) => (
                                          <tr key={i}>
                                            <Td>{d.bon_number || "—"}</Td>
                                            <TdCenter>{d.unp || "—"}</TdCenter>
                                            <Td>{d.doc_datetime ? fmtDeviceDateTimeBG(d.doc_datetime) : "—"}</Td>
                                            <Td>{d.doc_no || "—"}</Td>
                                            <TdRight>{String(d.qty)}</TdRight>
                                            <TdRight>{formatMainWithSecondary(Number(d.unit_price || 0), rowSaleDate(d) || reportAnchorDate)}</TdRight>
                                            <TdRight>{formatMainWithSecondary(Number(d.sum || 0), rowSaleDate(d) || reportAnchorDate)}</TdRight>
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
      
                    {/* separator before totals */}
                    <tr aria-hidden="true">
                      <td colSpan={4} style={{ padding: 0, borderBottom: 0 }}>
                        <div style={{ borderTop: "2px solid #999" }} />
                      </td>
                    </tr>
                  </tbody>
      
                  <tfoot>
                    <tr>
                      <Td style={{ fontWeight: 800, borderTop: 0 }}>Общо — Бързи продажби</Td>
                      <TdRight style={{ fontWeight: 800, borderTop: 0 }}>
                        {String(quickData.totals.qty)}
                      </TdRight>
                      <TdRight style={{ borderTop: 0 }} />
                      <TdRight style={{ fontWeight: 800, borderTop: 0 }}>
                        {formatMainWithSecondary(quickData.totals.sum, reportAnchorDate)}
                      </TdRight>
                    </tr>
                  </tfoot>
                </TableShell>
              </Box>
            )}
      



{/* Фискални бонове (групирани със сторно вътре) */}
{(printedBonsList.length > 0 || stornoBons.length > 0) && (
  <Box sx={{ mb: 1.5, p: 1, border: "1px solid #e8e8f3", borderRadius: 2, bgcolor: "#fff" }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
      Фискални бонове (печатани за периода) — със сторно
    </Typography>

    <TableShell>
      <colgroup>
        <col style={{ width: "4%" }} />
        <col style={{ width: "18%" }} />
        <col style={{ width: "18%" }} />
        <col style={{ width: "16%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "14%" }} />
        <col style={{ width: "10%" }} />
      </colgroup>

      <thead>
        <tr>
          <ThCenter />
          <Th>Дата/час</Th>
          <Th>Фискален №</Th>
          <ThCenter>УНП</ThCenter>
          <ThRight>Сума</ThRight>
          <Th>Плащане</Th>
          <Th>Потребител</Th>
          <Th>Статус</Th>
        </tr>
      </thead>

      <tbody>
        {printedBonsList.map((b) => {
          const key = `bon|${b.unp}`;
          const open = isOpen(key);
          const stornoList = stornoByUnp.get(String(b.unp || "").trim()) || [];
          const canExpand = stornoList.length > 0;

          const focused = !!(stornoFocusUnp && String(b.unp || "").trim() === String(stornoFocusUnp || "").trim());
          const canceled = !!b.isCanceled;
          const partial = !!b.isPartial;
          const bg = focused ? "#ffd9d9" : (canceled ? "#fff1f1" : (partial ? "#fff8e1" : undefined));
          const color = canceled ? "#b00020" : (partial ? "#8a6d00" : undefined);

          const dt = b.doc_datetime ? fmtDeviceDateTimeBG(b.doc_datetime) : fmtBGDateTime(b.bon_datetime);

          return (
            <React.Fragment key={b.unp}>
              <tr
                ref={(el) => { if (el) bonRowRefs.current.set(String(b.unp || "").trim(), el); }}
                style={{ background: bg, color }}
              >
                <TdCenter
                  onClick={(e) => { e.stopPropagation(); if (canExpand) toggleOpen(key); }}
                  style={{ cursor: canExpand ? "pointer" : "default" }}
                  title={canExpand ? "Покажи/скрий сторно" : ""}
                >
                  {canExpand ? <Caret open={open} /> : null}
                </TdCenter>

                <Td onClick={() => { if (canExpand) toggleOpen(key); }} style={{ cursor: canExpand ? "pointer" : "default" }}>
                  {dt || "—"}
                </Td>
                <Td onClick={() => { if (canExpand) toggleOpen(key); }} style={{ cursor: canExpand ? "pointer" : "default" }}>
                  {b.bon_number || "—"}
                </Td>
                <TdCenter onClick={() => { if (canExpand) toggleOpen(key); }} style={{ cursor: canExpand ? "pointer" : "default" }}>
                  {b.unp || "—"}
                </TdCenter>
                <TdRight onClick={() => { if (canExpand) toggleOpen(key); }} style={{ cursor: canExpand ? "pointer" : "default" }}>
                  {formatMainWithSecondary(Number((b.net_sum ?? b.sum) || 0), reportAnchorDate)}
                </TdRight>
                <Td onClick={() => { if (canExpand) toggleOpen(key); }} style={{ cursor: canExpand ? "pointer" : "default" }}>
                  {String(b.payment_method || "").toLowerCase() || "—"}
                </Td>
                <Td onClick={() => { if (canExpand) toggleOpen(key); }} style={{ cursor: canExpand ? "pointer" : "default" }}>
                  {b.username || "—"}
                </Td>
                <Td onClick={() => { if (canExpand) toggleOpen(key); }} style={{ cursor: canExpand ? "pointer" : "default" }}>
                  {canceled ? "СТОРНИРАН" : (b.isPartial ? "ЧАСТИЧНО" : "OK")}
                </Td>
              </tr>

              {open && stornoList.length > 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 0, background: "#fff" }}>
                    <div style={{ padding: "8px 10px" }}>
                      <div
                        style={{
                          border: "1px solid #f3b5b5",
                          background: "#ffecec",
                          borderRadius: 10,
                          padding: "8px 10px",
                        }}
                      >
                        <div style={{ fontWeight: 800, color: "#b00020", marginBottom: 6 }}>
                          Сторно към този бон
                        </div>
              
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr>
                              <Th>Дата/час</Th>
                              <Th>Фискален №</Th>
                              <ThRight>Сума</ThRight>
                              <Th>Плащане</Th>
                              <Th>Потребител</Th>
                              <Th>Сторно на (УНП/бон)</Th>
                            </tr>
                          </thead>
                          <tbody>
                            {stornoList.map((s, i) => {
                              const ref = String(
                                s?.storno_of_unp ||
                                  s?.storno_of_unp_code ||
                                  s?.storno_of_bon_number ||
                                  s?.storno_of_bon_id ||
                                  ""
                              ).trim();
              
                              return (
                                <tr key={i} style={{ background: "#fff1f1" }}>
                                  <Td>{fmtBGDateTime(s.bon_datetime)}</Td>
                                  <Td>{s.bon_number || "—"}</Td>
                                  <TdRight>
                                    {formatMainWithSecondary(
                                      Number(s.total_amount || 0),
                                      rowSaleDate(s) || reportAnchorDate
                                    )}
                                  </TdRight>
                                  <Td>{String(s.payment_method || "").toLowerCase() || "—"}</Td>
                                  <Td>{s.username || "—"}</Td>
                                  <Td>{ref || "—"}</Td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              
            </React.Fragment>
          );
        })}

        {/* separator before footer */}
        <tr aria-hidden="true">
          <td colSpan={8} style={{ padding: 0, borderBottom: 0 }}>
            <div style={{ borderTop: "2px solid #999" }} />
          </td>
        </tr>
      </tbody>

      <tfoot>
        <tr>
          <Td colSpan={3} style={{ fontWeight: 800, borderTop: 0 }}>
            Общо — Фискални бонове
          </Td>
          <TdCenter style={{ fontWeight: 800, borderTop: 0 }}>
            {String(printedBonsList.length)}
          </TdCenter>
          <TdRight style={{ fontWeight: 800, borderTop: 0 }}>
            {formatMainWithSecondary(
              printedBonsList.reduce((a, b) => a + Number((b.net_sum ?? b.sum) || 0), 0),
              reportAnchorDate
            )}
          </TdRight>
          <Td colSpan={3} style={{ fontWeight: 800, borderTop: 0 }}>
            Сторно: {String(stornoTotals.count)} · {formatMainWithSecondary(stornoTotals.sum, reportAnchorDate)}
          </Td>
        </tr>
      </tfoot>
    </TableShell>

    {/* Unmatched storno (references not found in printed bons list) */}
    {stornoUnmatched.length > 0 && (
      <Box sx={{ mt: 1, p: 1, border: "1px solid #f0bcbc", borderRadius: 2, bgcolor: "#fff1f1" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#b00020", mb: 0.5 }}>
          Сторно без намерен оригинален бон (за периода)
        </Typography>

        <TableShell bg="#fff1f1" borderColor="#f0bcbc">
          <colgroup>
            <col style={{ width: "18%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "18%" }} />
          </colgroup>
          <thead>
            <tr>
              <Th>Дата/час</Th>
              <Th>Фискален №</Th>
              <ThRight>Сума</ThRight>
              <Th>Плащане</Th>
              <Th>Потребител</Th>
              <Th>Сторно на (УНП/бон)</Th>
            </tr>
          </thead>
          <tbody>
            {stornoUnmatched.map((s, i) => {
              const refUnp = String(
                s?.storno_of_unp || s?.storno_of_unp_code || s?.storno_of_bon_number || s?.storno_of_bon_id || ""
              ).trim();
              return (
                <tr key={i} style={{ background: "#fff1f1", color: "#b00020" }}>
                  <Td>{fmtBGDateTime(s.bon_datetime) || "—"}</Td>
                  <Td>{s.bon_number || "—"}</Td>
                  <TdRight>{formatMainWithSecondary(Number(s.total_amount || 0), rowSaleDate(s) || reportAnchorDate)}</TdRight>
                  <Td>{(s.payment_method || "").toLowerCase()}</Td>
                  <Td>{s.username || "—"}</Td>
                  <Td>{refUnp || "—"}</Td>
                </tr>
              );
            })}
          </tbody>
        </TableShell>
      </Box>
    )}
  </Box>
)}

        {/* fallback summary table */}
        <TableShell padCell={padCell} thPad={thPad}>
          <thead>
            <tr>
              <Th pad={thPad}>Група</Th>
              <ThRight pad={thPad}>Сума</ThRight>
              <ThRight pad={thPad}>Билети</ThRight>
              <ThRight pad={thPad}>Бар</ThRight>
              <ThRight pad={thPad}>Бърза</ThRight>
              <ThRight pad={thPad}>Бр.</ThRight>
            </tr>
          </thead>
          <tbody>
            {simpleData.list.map((g) => (
              <tr key={g.key}>
                <Td pad={padCell}>{g.key}</Td>
                <TdRight pad={padCell}>{formatMainWithSecondary(g.sum, reportAnchorDate)}</TdRight>
                <TdRight pad={padCell}>{formatMainWithSecondary(g.tickets, reportAnchorDate)}</TdRight>
                <TdRight pad={padCell}>{formatMainWithSecondary(g.bar, reportAnchorDate)}</TdRight>
                <TdRight pad={padCell}>{formatMainWithSecondary(g.quick, reportAnchorDate)}</TdRight>
                <TdRight pad={padCell}>{g.qty}</TdRight>
              </tr>
            ))}
            {!simpleData.list.length && !loading && (
              <tr>
                <Td pad={padCell} colSpan={6} style={{ opacity: 0.7 }}>
                  Няма данни за избраните филтри.
                </Td>
              </tr>
            )}
          </tbody>
        </TableShell>
      </Box>
    </LocalizationProvider>
  );
}
